// frontend/src/components/CanvasArea/CanvasArea.js

import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import Konva from 'konva';
import './CanvasArea.css';
import {
  Stage,
  Layer,
  Rect,
  Circle,
  Wedge,
  RegularPolygon,
  Star,
  Transformer,
  Image,
  Text,
  Line,
  Group,
  Shape,
} from 'react-konva';
import useImage from 'use-image';
import useFontLoader from '../../hooks/useFontLoader';
import { APP_FONT_FAMILIES } from '../../utils/fontPreloader';
import cursorDefault from '../../assets/cursors/cursor-default.png';
import cursorPencil from '../../assets/cursors/cursor-pencil.png';
import cursorPen from '../../assets/cursors/cursor-pen.png';
import cursorText from '../../assets/cursors/cursor-text.png';
import cursorCrosshair from '../../assets/cursors/cursor-crosshair.png';
import cursorHandOpen from '../../assets/cursors/cursor-hand-open.png';
import cursorHandGrab from '../../assets/cursors/cursor-hand-grab.png';
import cursorRotate from '../../assets/cursors/cursor-rotate.png';
import cursorResizeHorizontal from '../../assets/cursors/cursor-resize-horizontal.png';
import cursorResizeVertical from '../../assets/cursors/cursor-resize-vertical.png';
import cursorResizeDiagonal1 from '../../assets/cursors/cursor-resize-diagonal-1.png';
import cursorResizeDiagonal2 from '../../assets/cursors/cursor-resize-diagonal-2.png';
import cursorPaintBucket from '../../assets/cursors/cursor-paint-bucket.png';

const fillPatternCache = new Map();

const getShapeFillBounds = (shape) => {
  if (Number.isFinite(shape.width) && Number.isFinite(shape.height)) {
    return { x: 0, y: 0, width: Math.max(1, Math.abs(shape.width)), height: Math.max(1, Math.abs(shape.height)) };
  }

  const coordinatePoints = Array.isArray(shape.bezierPoints) && shape.bezierPoints.length
    ? shape.bezierPoints.flatMap((point) => [point.x, point.y])
    : shape.points;

  if (Array.isArray(coordinatePoints) && coordinatePoints.length >= 4) {
    const xs = coordinatePoints.filter((_, index) => index % 2 === 0).map(Number).filter(Number.isFinite);
    const ys = coordinatePoints.filter((_, index) => index % 2 === 1).map(Number).filter(Number.isFinite);
    if (xs.length && ys.length) {
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      return { x: minX, y: minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) };
    }
  }

  const radius = Math.max(1, Math.abs(shape.radius || shape.outerRadius || 1));
  return { x: -radius, y: -radius, width: radius * 2, height: radius * 2 };
};

const makeFillPattern = (style, background, foreground, size, spacing, rotation) => {
  if (typeof document === 'undefined') return null;

  const safeSize = Math.max(3, Math.min(60, Number(size) || 10));
  const safeSpacing = Math.max(0, Math.min(60, Number(spacing) || 0));
  const tileSize = Math.max(8, Math.ceil(safeSize + safeSpacing));
  const key = [style, background, foreground, safeSize, safeSpacing, rotation].join('|');
  if (fillPatternCache.has(key)) return fillPatternCache.get(key);

  const canvas = document.createElement('canvas');
  canvas.width = tileSize;
  canvas.height = tileSize;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, tileSize, tileSize);
  ctx.strokeStyle = foreground;
  ctx.fillStyle = foreground;
  ctx.lineWidth = Math.max(1, safeSize * 0.18);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const c = tileSize / 2;
  const r = Math.min(safeSize / 2, tileSize * 0.42);

  ctx.save();

  if (style === 'dots') {
    ctx.beginPath(); ctx.arc(c, c, r, 0, Math.PI * 2); ctx.fill();
  } else if (style === 'squares') {
    ctx.fillRect(c - r, c - r, r * 2, r * 2);
  } else if (style === 'hearts') {
    const heartSize = r * 1.7;
    ctx.beginPath();
    ctx.moveTo(c, c + heartSize * 0.65);
    ctx.bezierCurveTo(c - heartSize, c, c - heartSize * 0.55, c - heartSize * 0.65, c, c - heartSize * 0.18);
    ctx.bezierCurveTo(c + heartSize * 0.55, c - heartSize * 0.65, c + heartSize, c, c, c + heartSize * 0.65);
    ctx.fill();
  } else if (style === 'stars') {
    ctx.beginPath();
    for (let i = 0; i < 10; i += 1) {
      const angle = -Math.PI / 2 + (i * Math.PI) / 5;
      const pointRadius = i % 2 === 0 ? r : r * 0.45;
      const x = c + Math.cos(angle) * pointRadius;
      const y = c + Math.sin(angle) * pointRadius;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath(); ctx.fill();
  } else if (style === 'lines') {
    ctx.beginPath(); ctx.moveTo(0, c); ctx.lineTo(tileSize, c); ctx.stroke();
  } else if (style === 'grid') {
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(tileSize, 0);
    ctx.moveTo(0, 0); ctx.lineTo(0, tileSize);
    ctx.stroke();
  } else if (style === 'checks') {
    ctx.fillRect(0, 0, c, c); ctx.fillRect(c, c, c, c);
  } else if (style === 'diagonalLines') {
    ctx.beginPath();
    ctx.moveTo(-tileSize * 0.25, tileSize); ctx.lineTo(tileSize * 0.75, 0);
    ctx.moveTo(tileSize * 0.25, tileSize); ctx.lineTo(tileSize * 1.25, 0);
    ctx.stroke();
  } else if (style === 'crosshatch') {
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(tileSize, tileSize);
    ctx.moveTo(tileSize, 0); ctx.lineTo(0, tileSize);
    ctx.stroke();
  } else if (style === 'waves') {
    ctx.beginPath();
    ctx.moveTo(0, c);
    ctx.bezierCurveTo(tileSize * 0.25, c - r, tileSize * 0.25, c + r, tileSize * 0.5, c);
    ctx.bezierCurveTo(tileSize * 0.75, c - r, tileSize * 0.75, c + r, tileSize, c);
    ctx.stroke();
  } else if (style === 'zigzags') {
    ctx.beginPath(); ctx.moveTo(0, c + r); ctx.lineTo(c, c - r); ctx.lineTo(tileSize, c + r); ctx.stroke();
  } else if (style === 'confetti') {
    ctx.lineWidth = Math.max(1.5, safeSize * 0.2);
    ctx.beginPath();
    ctx.moveTo(tileSize * 0.15, tileSize * 0.25); ctx.lineTo(tileSize * 0.35, tileSize * 0.12);
    ctx.moveTo(tileSize * 0.62, tileSize * 0.24); ctx.lineTo(tileSize * 0.75, tileSize * 0.42);
    ctx.moveTo(tileSize * 0.25, tileSize * 0.7); ctx.lineTo(tileSize * 0.42, tileSize * 0.82);
    ctx.moveTo(tileSize * 0.72, tileSize * 0.68); ctx.lineTo(tileSize * 0.88, tileSize * 0.58);
    ctx.stroke();
  }

  ctx.restore();
  fillPatternCache.set(key, canvas);
  return canvas;
};

const getShapeFillProps = (shape) => {
  const style = shape.fillStyle || 'solid';
  const primary = shape.fillColor || '#ffffff';
  const secondary = shape.fillSecondaryColor || '#F2D6A7';
  const bounds = getShapeFillBounds(shape);

  if (style === 'solid') return { fill: primary, fillPriority: 'color' };

  if (style === 'horizontalGradient' || style === 'verticalGradient' || style === 'diagonalGradient') {
    const endPoint = style === 'horizontalGradient'
      ? { x: bounds.x + bounds.width, y: bounds.y }
      : style === 'verticalGradient'
        ? { x: bounds.x, y: bounds.y + bounds.height }
        : { x: bounds.x + bounds.width, y: bounds.y + bounds.height };

    return {
      fill: primary,
      fillPriority: 'linear-gradient',
      fillLinearGradientStartPoint: { x: bounds.x, y: bounds.y },
      fillLinearGradientEndPoint: endPoint,
      fillLinearGradientColorStops: [0, primary, 1, secondary],
    };
  }

  if (style === 'radialGradient') {
    const centre = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
    return {
      fill: primary,
      fillPriority: 'radial-gradient',
      fillRadialGradientStartPoint: centre,
      fillRadialGradientStartRadius: 0,
      fillRadialGradientEndPoint: centre,
      fillRadialGradientEndRadius: Math.max(bounds.width, bounds.height) / 2,
      fillRadialGradientColorStops: [0, primary, 1, secondary],
    };
  }

  const pattern = makeFillPattern(
    style,
    primary,
    secondary,
    shape.fillPatternSize,
    shape.fillPatternSpacing,
    shape.fillPatternRotation
  );

  return pattern
    ? {
        fill: primary,
        fillPriority: 'pattern',
        fillPatternImage: pattern,
        fillPatternRepeat: 'repeat',
        fillPatternRotation: Number(shape.fillPatternRotation) || 0,
      }
    : { fill: primary, fillPriority: 'color' };
};


const ImageShape = ({ shape, onLoad, logoUrl, squareLogoUrl, ...props }) => {
  const isRectLogoPlaceholder = shape.src === '__USER_LOGO__';
  const isSquareLogoPlaceholder = shape.src === '__USER_SQUARE_LOGO__';

  const resolvedSrc = isRectLogoPlaceholder
    ? logoUrl
    : isSquareLogoPlaceholder
    ? squareLogoUrl
    : shape.src;

  // Always use Anonymous for anything that may be exported by Konva.
  // This keeps the canvas export-safe, as long as the image server
  // sends proper CORS headers.
  const crossOrigin = 'Anonymous';

  const [image, status] = useImage(resolvedSrc || '', crossOrigin);
  
    
  useEffect(() => {
    if (status === 'loaded' && onLoad) {
      onLoad();
    }
  }, [status, onLoad]);

  if (!resolvedSrc) {
    return <Text text="Loading..." x={shape.x} y={shape.y} />;
  }

  if (status === 'loading') {
    return <Text text="Loading..." x={shape.x} y={shape.y} />;
  }

  if (status === 'error') {
    return (
      <Text
        text="Failed to load image"
        x={shape.x}
        y={shape.y}
        fill="red"
      />
    );
  }

  return (
    <Image
      image={image}
      width={shape.width}
      height={shape.height}
      offsetX={shape.width / 2}
      offsetY={shape.height / 2}
      opacity={shape.opacity}
      {...props}
    />
  );
};

  
const CanvasBurst = ({ burst, stageRef, setCanvasBursts }) => {
  const ringRef = useRef(null);
  const particleRefs = useRef([]);
  const imagePopTweenRef = useRef(null);

  useEffect(() => {
    const tweens = [];

    if (ringRef.current) {
      ringRef.current.scale({ x: 0.2, y: 0.2 });
      ringRef.current.opacity(0.9);

      const ringTween = new Konva.Tween({
        node: ringRef.current,
        duration: 0.38,
        scaleX: 1.9,
        scaleY: 1.9,
        opacity: 0,
        easing: Konva.Easings.EaseOut,
      });

      ringTween.play();
      tweens.push(ringTween);
    }

    const particleDistance = burst.radius + 14;
      particleRefs.current.forEach((node, index) => {
        if (!node) return;

        const angle = (Math.PI * 2 * index) / particleRefs.current.length;
        const offsetX = Math.cos(angle) * particleDistance;
        const offsetY = Math.sin(angle) * particleDistance;

        node.x(burst.x);
        node.y(burst.y);
        node.scale({ x: 0.6, y: 0.6 });
        node.opacity(0.95);

        const particleTween = new Konva.Tween({
          node,
          duration: 0.42,
          x: burst.x + offsetX,
          y: burst.y + offsetY,
          scaleX: 0.2,
          scaleY: 0.2,
          opacity: 0,
          easing: Konva.Easings.EaseOut,
        });

        particleTween.play();
        tweens.push(particleTween);
      });

    const stage = stageRef.current;
    const imageNode = stage?.findOne(`#${burst.shapeId}`);

    if (imageNode) {
      const startScaleX = imageNode.scaleX();
      const startScaleY = imageNode.scaleY();

      imagePopTweenRef.current = new Konva.Tween({
        node: imageNode,
        duration: 0.14,
        scaleX: startScaleX * 1.06,
        scaleY: startScaleY * 1.06,
        easing: Konva.Easings.EaseOut,
        onFinish: () => {
          const settleTween = new Konva.Tween({
            node: imageNode,
            duration: 0.16,
            scaleX: startScaleX,
            scaleY: startScaleY,
            easing: Konva.Easings.EaseInOut,
          });

          settleTween.play();
          tweens.push(settleTween);
        },
      });

      imagePopTweenRef.current.play();
      tweens.push(imagePopTweenRef.current);
    }

    const cleanupTimer = window.setTimeout(() => {
      setCanvasBursts((prev) => prev.filter((item) => item.id !== burst.id));
    }, 520);

    return () => {
      window.clearTimeout(cleanupTimer);
      tweens.forEach((tween) => tween?.destroy());
    };
  }, [burst, stageRef, setCanvasBursts]);

  return (
    <>
      <Circle
        ref={ringRef}
        x={burst.x}
        y={burst.y}
        radius={burst.radius}
        stroke="#22c55e"
        strokeWidth={5}
        listening={false}
      />

      {Array.from({ length: 8 }).map((_, index) => (
        <Circle
          key={`${burst.id}-particle-${index}`}
          ref={(node) => {
            particleRefs.current[index] = node;
          }}
          x={burst.x}
          y={burst.y}
          radius={4}
          fill="#22c55e"
          listening={false}
        />
      ))}
    </>
  );
};

const buildFancyRectStrokePoints = (width, height, style, size = 8, waveCount = 6) => {
  const points = [];
  const safeSize = Math.max(1, size || 8);
  const safeWaveCount = Math.max(1, waveCount || 6);

  const addEdge = (x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.max(1, Math.hypot(dx, dy));

    const nx = -dy / length;
    const ny = dx / length;

    const steps =
      style === 'wavy'
        ? Math.max(12, safeWaveCount * 16)
        : Math.max(4, safeWaveCount * 2);

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = x1 + dx * t;
      const y = y1 + dy * t;

      let offset = 0;

      if (style === 'wavy') {
        offset = Math.sin(t * Math.PI * 2 * safeWaveCount) * safeSize;
      }

      if (style === 'zigzag') {
        offset = i % 2 === 0 ? -safeSize : safeSize;
      }

      points.push(x + nx * offset, y + ny * offset);
    }
  };

  addEdge(0, 0, width, 0);
  addEdge(width, 0, width, height);
  addEdge(width, height, 0, height);
  addEdge(0, height, 0, 0);

  return points;
};

const isPointNearStrokeOnlyShape = (shape, pointer, zoomScale = 1) => {
  if (!shape || !pointer) return true;

  const strokeWidth = shape.strokeWidth || 2;
  const zoom = zoomScale || 1;
  const tolerance = Math.max(10 / zoom, (strokeWidth + 8) / zoom);

  const scaleX = shape.scaleX || 1;
  const scaleY = shape.scaleY || 1;

  // Convert stage pointer into the shape's unscaled local space
  const localX = (pointer.x - (shape.x || 0)) / scaleX;
  const localY = (pointer.y - (shape.y || 0)) / scaleY;

  if (shape.type === 'rect' || shape.type === 'roundedRect') {
    const width = shape.width || 0;
    const height = shape.height || 0;

    const adjustedToleranceX = tolerance / Math.abs(scaleX || 1);
    const adjustedToleranceY = tolerance / Math.abs(scaleY || 1);

    const isNearRectBounds =
      localX >= -adjustedToleranceX &&
      localX <= width + adjustedToleranceX &&
      localY >= -adjustedToleranceY &&
      localY <= height + adjustedToleranceY;

    if (!isNearRectBounds) {
      return false;
    }

    return (
      localX <= adjustedToleranceX ||
      localX >= width - adjustedToleranceX ||
      localY <= adjustedToleranceY ||
      localY >= height - adjustedToleranceY
    );
  }

  if (
    ['circle', 'semiCircle', 'triangle', 'pentagon', 'hexagon', 'octagon', 'star'].includes(shape.type)
  ) {
    const radius =
      shape.radius ||
      shape.outerRadius ||
      Math.max(shape.width || 0, shape.height || 0) / 2;

    const averageScale = (Math.abs(scaleX) + Math.abs(scaleY)) / 2 || 1;
    const adjustedTolerance = tolerance / averageScale;

    const distance = Math.hypot(localX, localY);

    return Math.abs(distance - radius) <= adjustedTolerance;
  }

  return true;
};

const findShapeUnderPointer = (shapes, pointer, zoomScale = 1) => {
  if (!Array.isArray(shapes) || !pointer) return null;

  for (let i = shapes.length - 1; i >= 0; i--) {
    const shape = shapes[i];

    if (!shape || shape.locked) continue;

    const isStrokeOnlyShape =
      shape.fillEnabled === false &&
      shape.strokeEnabled !== false &&
      [
        'rect',
        'roundedRect',
        'circle',
        'semiCircle',
        'triangle',
        'pentagon',
        'hexagon',
        'octagon',
        'star',
      ].includes(shape.type);

    if (isStrokeOnlyShape) {
      if (isPointNearStrokeOnlyShape(shape, pointer, zoomScale)) {
        return shape;
      }

      continue;
    }

    const scaleX = shape.scaleX || 1;
    const scaleY = shape.scaleY || 1;
    const localX = (pointer.x - (shape.x || 0)) / scaleX;
    const localY = (pointer.y - (shape.y || 0)) / scaleY;

    if (
      [
        'rect',
        'roundedRect',
        'grid',
        'maze',
        'wordSearch',
      ].includes(shape.type)
    ) {
      if (
        localX >= 0 &&
        localX <= (shape.width || 0) &&
        localY >= 0 &&
        localY <= (shape.height || 0)
      ) {
        return shape;
      }
    }

    if (
      [
        'circle',
        'semiCircle',
        'circleSegments',
        'triangle',
        'pentagon',
        'hexagon',
        'octagon',
        'star',
      ].includes(shape.type)
    ) {
      const radius =
        shape.radius ||
        shape.outerRadius ||
        Math.max(shape.width || 0, shape.height || 0) / 2;

      if (Math.hypot(localX, localY) <= radius) {
        return shape;
      }
    }

    if (shape.type === 'image') {
      const width = shape.width || 0;
      const height = shape.height || 0;

      if (
        localX >= -width / 2 &&
        localX <= width / 2 &&
        localY >= -height / 2 &&
        localY <= height / 2
      ) {
        return shape;
      }
    }

    if (shape.type === 'text') {
      if (
        localX >= 0 &&
        localX <= (shape.width || 200) &&
        localY >= 0 &&
        localY <= (shape.height || shape.fontSize || 30)
      ) {
        return shape;
      }
    }
  }

  return null;
};

const CanvasArea = ({
  pages,
  setPages,
  activePage,
  selectedTool,
  setSelectedTool,
  selectedShapeIds,
  setSelectedShapeIds,
  selectionRect,
  setSelectionRect,
  isSelecting,
  setIsSelecting,
  stageRef,
  transformerRef,
  panOffset,
  scale,
  drawing,
  setDrawing,
  handleStageMouseDown,
  handleStageMouseMove,
  handleStageMouseUp,
  isShiftPressed,
  isEditingText,
  editingTextPosition,
  editingTextId,
  editingTextValue,
  updateTextContentHandler,
  setIsEditingText,
  textProperties,
  handleShapeClick,
  handleTextDoubleClick,
  handleShapeDragStart,
  handleShapeDragEnd,
  handleTransformEnd,
  isGridVisible,
  isQuartersGridVisible,
  isThirdsGridVisible,
  isRulerGridVisible,
  setIsRulerGridVisible,

  isMeasurementRulerVisible,
  isMeasurementRulerSelected,
  setIsMeasurementRulerSelected,
  measurementRulerSettings,
  setMeasurementRulerSettings,

  isCustomGridVisible,
  customGridSettings,
  isEditMode,
  pushToUndoStack,
  isExporting,  
  penPoints,
  setPenPoints,
  penTempLineId,
  setPenTempLineId,
  penMousePos,
  setPenMousePos,
  penToolSettings,
  logoUrl,
  squareLogoUrl,
  canvasBursts,
  setCanvasBursts,
  isMiddleGrabbing,
  selectWordSearchCell,
  clearWordSearchCellSelection,
  bucketSettings,
  applyBucketToShape,
  applyBucketToGridCell,
  applyBucketToCircleSegment,
  paintWordSearchCellText,
}) => {
  const initialPositionsRef = useRef({});
  const [imageLoadCounter, setImageLoadCounter] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedBezierPoint, setSelectedBezierPoint] = useState(null);
  const transformStartedRef = useRef(false);
  const clickThroughDragRef = useRef(null);
  const bezierPendingDragRef = useRef(null);
  const bezierAnchorDragStartRef = useRef(null);
  const textEditorRef = useRef(null);
  const textMeasureRef = useRef(null);
  const measurementRulerRef = useRef(null);
  const measurementRulerTransformerRef = useRef(null);
  
  const bucketDraggingRef = useRef(false);
  const bucketPaintedCellsRef = useRef(new Set());
  const wordSearchTextDraggingRef = useRef(false);
  const wordSearchTextPaintedCellsRef = useRef(new Set());
  const onImageLoad = useCallback(() => {
      setImageLoadCounter((prev) => prev + 1);
    }, []);

  const clearAllWordSearchSelections = useCallback(() => {
    setPages((prevPages) =>
      prevPages.map((page, pageIndex) => {
        if (pageIndex !== activePage) return page;

        let changed = false;

        const updatedShapes = page.shapes.map((shape) => {
          if (
            shape.type === 'wordSearch' &&
            shape.wordSearchSelectedCellIndex !== null &&
            shape.wordSearchSelectedCellIndex !== undefined
          ) {
            changed = true;
            return {
              ...shape,
              wordSearchSelectedCellIndex: null,
            };
          }

          return shape;
        });

        return changed ? { ...page, shapes: updatedShapes } : page;
      })
    );
  }, [setPages, activePage]);

    const getSnappedPenPoint = (point, anchorPoint) => {
    if (!anchorPoint) return point;

    const dx = point.x - anchorPoint[0];
    const dy = point.y - anchorPoint[1];
    const distance = Math.hypot(dx, dy);

    if (distance === 0) {
      return {
        x: anchorPoint[0],
        y: anchorPoint[1],
      };
    }

    const angle = Math.atan2(dy, dx);
    const snappedAngle = Math.round(angle / (Math.PI / 8)) * (Math.PI / 8);

    return {
      x: anchorPoint[0] + Math.cos(snappedAngle) * distance,
      y: anchorPoint[1] + Math.sin(snappedAngle) * distance,
    };
  };

  const finishPenPath = useCallback((closed = false) => {
    if (penPoints.length < 2) return;

    const sourcePoints = [...penPoints];

    const minX = penToolSettings.bezierEnabled
      ? Math.min(...sourcePoints.map((p) => p[0]))
      : 0;

    const minY = penToolSettings.bezierEnabled
      ? Math.min(...sourcePoints.map((p) => p[1]))
      : 0;

    const localPoints = sourcePoints.map((p) => ({
      x: p[0] - minX,
      y: p[1] - minY,
    }));

    const newShape = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type: 'pen',
      x: penToolSettings.bezierEnabled ? minX : 0,
      y: penToolSettings.bezierEnabled ? minY : 0,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      locked: false,
      points: penToolSettings.bezierEnabled
        ? localPoints.flatMap((p) => [p.x, p.y])
        : sourcePoints.flat(),
      bezierEnabled: penToolSettings.bezierEnabled || false,
      bezierPoints: penToolSettings.bezierEnabled
        ? localPoints.map((point, index) => {
            const prevPoint = localPoints[Math.max(0, index - 1)];
            const nextPoint = localPoints[Math.min(localPoints.length - 1, index + 1)];

            const dx = nextPoint.x - prevPoint.x;
            const dy = nextPoint.y - prevPoint.y;
            const length = Math.max(1, Math.hypot(dx, dy));
            const handleLength = Math.min(35, length * 0.2);

            return {
              x: point.x,
              y: point.y,
              in: {
                x: index === 0 && !closed ? 0 : (-dx / length) * handleLength,
                y: index === 0 && !closed ? 0 : (-dy / length) * handleLength,
              },
              out: {
                x:
                  index === localPoints.length - 1 && !closed
                    ? 0
                    : (dx / length) * handleLength,
                y:
                  index === localPoints.length - 1 && !closed
                    ? 0
                    : (dy / length) * handleLength,
              },
            };
          })
        : undefined,
      closed,
      strokeWidth: penToolSettings.strokeWidth || 2,
      strokeColor: penToolSettings.strokeColor || '#000000',
      fillEnabled: closed ? penToolSettings.fillEnabled ?? true : false,
      fillColor: penToolSettings.fillColor || '#ffffff',
      strokeEnabled: penToolSettings.strokeEnabled ?? true,
      opacity: penToolSettings.opacity ?? 1,
      dashEnabled: penToolSettings.dashEnabled ?? false,
      dash: penToolSettings.dashEnabled
        ? (penToolSettings.dash?.length ? penToolSettings.dash : [10, 5])
        : [],
    };

    pushToUndoStack();

    const updatedPages = [...pages];
    updatedPages[activePage] = {
      ...pages[activePage],
      shapes: [...pages[activePage].shapes, newShape],
    };

    setPages(updatedPages);
    setSelectedShapeIds([newShape.id.toString()]);
    setSelectedTool('select');
    setPenPoints([]);
    setPenTempLineId(null);
    setPenMousePos(null);
  }, [
    penPoints,
    penToolSettings,
    pushToUndoStack,
    pages,
    activePage,
    setPages,
    setSelectedShapeIds,
    setSelectedTool,
    setPenPoints,
    setPenTempLineId,
  ]);
  
  // ✅ BLOCK tools on middle mouse press; still allow window-level pan
    const handleMouseDownInternal = (e) => {
      // Middle button? Do nothing (don’t call handleStageMouseDown)
      if (e?.evt?.button === 1) return;

      // Space-grab active? Do not let tools fire
      if (isMiddleGrabbing) return;

      const stage = stageRef.current;
      const pointerPos = stage.getPointerPosition();

      const clickedOnStage = e.target === stage;
      const clickedNodeId =
        typeof e.target?.id === 'function' ? e.target.id() : '';

      const clickedNode = e.target;
      const clickedParent = clickedNode?.getParent?.();
      const clickedGrandParent = clickedParent?.getParent?.();

      const clickedMeasurementRuler =
        clickedNodeId === 'measurement-ruler' ||
        clickedParent?.id?.() === 'measurement-ruler' ||
        clickedGrandParent?.id?.() === 'measurement-ruler';

      const clickedMeasurementRulerTransformer =
        clickedNode?.name?.()?.includes('_anchor') ||
        clickedParent?.className === 'Transformer' ||
        clickedGrandParent?.className === 'Transformer';

      if (clickedMeasurementRulerTransformer) {
        // Do not run normal canvas selection logic when pressing
        // the ruler's rotation handle.
        return;
      }

      if (!clickedMeasurementRuler) {
        setIsMeasurementRulerSelected(false);
      }

      const clickedShape = pages[activePage].shapes.find(
        (s) => s.id.toString() === clickedNodeId
      );

      // Clear any old pending Bezier drag when clicking something else
        if (
          bezierPendingDragRef.current &&
          bezierPendingDragRef.current.shapeId !== clickedNodeId
        ) {
          bezierPendingDragRef.current = null;
        }

      const isStrokeOnlyShape =
        selectedTool === 'select' &&
        clickedShape &&
        clickedShape.fillEnabled === false &&
        clickedShape.strokeEnabled !== false &&
        [
          'rect',
          'roundedRect',
          'circle',
          'semiCircle',
          'triangle',
          'pentagon',
          'hexagon',
          'octagon',
          'star',
        ].includes(clickedShape.type);

      if (isStrokeOnlyShape) {
        const pointer = stage.getPointerPosition();

        if (!isPointNearStrokeOnlyShape(clickedShape, pointer, scale)) {
          e.cancelBubble = true;
          e.target.stopDrag();

          const clickedShapeIndex = pages[activePage].shapes.findIndex(
            (shape) => shape.id.toString() === clickedShape.id.toString()
          );

          const shapeBelow = findShapeUnderPointer(
            pages[activePage].shapes.slice(0, clickedShapeIndex),
            pointer,
            scale
          );

          if (shapeBelow) {
            handleShapeClick(e, shapeBelow.id.toString());

            clickThroughDragRef.current = {
              shapeId: shapeBelow.id.toString(),
              startX: pointer.x,
              startY: pointer.y,
            };
          } else {
            setSelectedShapeIds([]);
            clickThroughDragRef.current = null;

            // Treat empty no-fill area like clicking the stage,
            // so drag-select can start underneath it.
            handleStageMouseDown({
              ...e,
              target: stage,
              passthroughShapeId: clickedShape.id.toString(),
            });
          }

          return;
        }
      }

      if (clickedOnStage || (clickedShape && clickedShape.type !== 'wordSearch')) {
        clearAllWordSearchSelections();
      }  

      if (selectedTool === 'pen') {
        const anchorPoint =
          penPoints.length > 0 ? penPoints[penPoints.length - 1] : null;

        const snappedPointerPos =
          isShiftPressed && anchorPoint
            ? getSnappedPenPoint(pointerPos, anchorPoint)
            : pointerPos;

        const newPoint = [snappedPointerPos.x, snappedPointerPos.y];

        if (penPoints.length > 2) {
          const [firstX, firstY] = penPoints[0];
          const distance = Math.hypot(
            snappedPointerPos.x - firstX,
            snappedPointerPos.y - firstY
          );

          if (distance < 10) {
            finishPenPath(true);
            return;
          }
        }

        const newPoints = [...penPoints, newPoint];
        setPenPoints(newPoints);
        return;
      }

      if (selectedTool === 'hand') {
        setIsDragging(true);
      }

      handleStageMouseDown(e);
    };


    // ✅ While middle button is held, ignore move for tools
    const handleMouseMoveInternal = (e) => {
      // e.evt.buttons uses a bitmask; middle button bit is 4
      if (e?.evt?.buttons === 4) return;

      // Space-grab active? Do not let tools fire
      if (isMiddleGrabbing) return;

      if (
        selectedTool === 'select' &&
        bezierPendingDragRef.current &&
        e?.evt?.buttons === 1
      ) {
        const stage = stageRef.current;
        const pointer = stage?.getPointerPosition();
        const pending = bezierPendingDragRef.current;

        if (pointer) {
          const distance = Math.hypot(
            pointer.x - pending.startX,
            pointer.y - pending.startY
          );

          if (distance > 3) {
            const node = stage.findOne(`#${pending.shapeId}`);

            if (node) {
              node.startDrag();
              handleDragStartInternal(pending.shapeId);
            }

            bezierPendingDragRef.current = null;
            return;
          }
        }
      }

      if (
        selectedTool === 'select' &&
        clickThroughDragRef.current &&
        e?.evt?.buttons === 1
      ) {
        const stage = stageRef.current;
        const pointer = stage?.getPointerPosition();
        const pending = clickThroughDragRef.current;

        if (pointer) {
          const distance = Math.hypot(
            pointer.x - pending.startX,
            pointer.y - pending.startY
          );

          if (distance > 3) {
            const node = stage.findOne(`#${pending.shapeId}`);

            if (node) {
              node.startDrag();
              handleDragStartInternal(pending.shapeId);
            }

            clickThroughDragRef.current = null;
            bezierPendingDragRef.current = null;
            return;
          }
        }
      }

      if (selectedTool === 'pen' && penPoints.length > 0) {
        const stage = stageRef.current;
        const pointerPos = stage.getPointerPosition();
        const anchorPoint = penPoints[penPoints.length - 1];

        const previewPoint =
          isShiftPressed && anchorPoint
            ? getSnappedPenPoint(pointerPos, anchorPoint)
            : pointerPos;

        setPenMousePos(previewPoint);
      }

      handleStageMouseMove(e);
    };

    // ✅ Ignore tool mouseup if it comes from middle button
    const handleMouseUpInternal = (e) => {
      if (e?.evt?.button === 1) return;

      // Space-grab active? Do not let tools fire
      if (isMiddleGrabbing) return;

      clickThroughDragRef.current = null;
      bezierPendingDragRef.current = null;

      if (selectedTool === 'bucket') {
        bucketDraggingRef.current = false;
        bucketPaintedCellsRef.current.clear();
      }

      wordSearchTextDraggingRef.current = false;
      wordSearchTextPaintedCellsRef.current.clear();

      if (selectedTool === 'hand') {
        setIsDragging(false);
      }

      handleStageMouseUp(e);
    };


    const handleTransformEndSnapping = (shapeId, node) => {
    const currentShapes = pages[activePage].shapes || [];

    const shape = currentShapes.find(
      (s) => s.id.toString() === shapeId
    );

    if (!shape || shape.locked) {
      return;
    }

    const selectedShapes = currentShapes.filter((s) =>
      selectedShapeIds.includes(s.id.toString())
    );

    const isMultiSelection = selectedShapeIds.length > 1;

    const selectionHasGroupedShape = selectedShapes.some(
      (s) => !!s.groupId
    );

    const selectionHasMaskedGroup = selectedShapes.some(
      (s) => s.type === 'maskedGroup'
    );

    const shouldAllowShiftSnap =
      isShiftPressed &&
      !isMultiSelection &&
      !selectionHasGroupedShape &&
      !selectionHasMaskedGroup &&
      shape.type !== 'maskedGroup' &&
      !shape.groupId;

    if (!transformStartedRef.current) {
      transformStartedRef.current = true;
      pushToUndoStack();
    }

    const updatedProps = {
      rotation: shouldAllowShiftSnap
        ? Math.round(node.rotation() / 45) * 45
        : node.rotation(),
      scaleX: node.scaleX(),
      scaleY: node.scaleY(),
      x: node.x(),
      y: node.y(),
    };

    handleTransformEnd(shapeId, updatedProps);
  };

  const createDragBoundFunc = (shapeId) => {
    return (pos) => {
      if (!isShiftPressed) {
        return pos;
      }

      const initialPos = initialPositionsRef.current[shapeId];
      if (!initialPos) {
        return pos;
      }

      const dx = pos.x - initialPos.x;
      const dy = pos.y - initialPos.y;

      if (Math.abs(dx) > Math.abs(dy)) {
        return { x: pos.x, y: initialPos.y };
      } else {
        return { x: initialPos.x, y: pos.y };
      }
    };
  };

  const handleDragStartInternal = (shapeId) => {
    handleShapeDragStart(shapeId);

    const shape = pages[activePage].shapes.find(
      (s) => s.id.toString() === shapeId
    );
    if (shape) {
      initialPositionsRef.current[shapeId] = { x: shape.x, y: shape.y };
    }
  };

  const handleDragEndInternal = (shapeId, newPos) => {
    handleShapeDragEnd({
      target: {
        id: shapeId,
        x: newPos.x,
        y: newPos.y,
      },
    });

    delete initialPositionsRef.current[shapeId];
  };

  const validateSelectedShapeIds = () => {
    const validIds = pages[activePage].shapes.map((shape) =>
      shape.id.toString()
    );
    const filteredSelectedIds = selectedShapeIds.filter((id) =>
      validIds.includes(id)
    );

    if (filteredSelectedIds.length !== selectedShapeIds.length) {
      console.warn(
        'Some selectedShapeIds do not correspond to any shapes and have been removed:',
        selectedShapeIds.filter((id) => !validIds.includes(id))
      );
      setSelectedShapeIds(filteredSelectedIds);
    }
  };

  useEffect(() => {
    // Reset pen if tool changes
    if (selectedTool !== 'pen' && penPoints.length > 0) {
      setPenPoints([]);
      setPenTempLineId(null);
    }
  }, [selectedTool]);

  useEffect(() => {
    validateSelectedShapeIds();
  }, [pages, activePage, selectedShapeIds]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (!selectedBezierPoint) return;
      if (isEditingText) return;

      const activeElement = document.activeElement;
      const activeTag = activeElement?.tagName?.toLowerCase();

      if (
        activeTag === 'input' ||
        activeTag === 'textarea' ||
        activeTag === 'select' ||
        activeElement?.isContentEditable
      ) {
        return;
      }

      const { shapeId, pointIndex } = selectedBezierPoint;

      const shape = pages[activePage]?.shapes?.find(
        (s) => s.id.toString() === shapeId.toString()
      );

      if (!shape || shape.locked || !Array.isArray(shape.bezierPoints)) return;

      if (shape.bezierPoints.length <= 3) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }

      pushToUndoStack();

      setPages((prevPages) =>
        prevPages.map((page, pageIndex) => {
          if (pageIndex !== activePage) return page;

          return {
            ...page,
            shapes: page.shapes.map((s) => {
              if (s.id.toString() !== shapeId.toString()) return s;

              const nextBezierPoints = s.bezierPoints.filter(
                (_, index) => index !== pointIndex
              );

              return {
                ...s,
                bezierPoints: nextBezierPoints,
                points: nextBezierPoints.flatMap((point) => [point.x, point.y]),
              };
            }),
          };
        })
      );

      setSelectedBezierPoint(null);
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [
    selectedBezierPoint,
    isEditingText,
    pages,
    activePage,
    setPages,
    pushToUndoStack,
  ]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (selectedTool !== 'pen') return;
      if (penPoints.length < 2) return;
      if (isEditingText) return;

      const activeElement = document.activeElement;
      const activeTag = activeElement?.tagName?.toLowerCase();

      if (
        activeTag === 'input' ||
        activeTag === 'textarea' ||
        activeTag === 'select' ||
        activeElement?.isContentEditable
      ) {
        return;
      }

      e.preventDefault();
      finishPenPath(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedTool, penPoints, isEditingText, finishPenPath]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (!isEditingText || !textEditorRef.current) return;

    const editor = textEditorRef.current;
    editor.innerText = editingTextValue || '';

    requestAnimationFrame(() => {
      const selection = window.getSelection();
      const range = document.createRange();

      range.selectNodeContents(editor);
      range.collapse(false);

      selection.removeAllRanges();
      selection.addRange(range);
    });
  }, [isEditingText, editingTextId]);   

  useEffect(() => {
    if (transformerRef.current) {
      const stage = stageRef.current;
      const selectedNodes = selectedShapeIds
        .map((id) => ({ id, node: stage.findOne(`#${id}`) }))
        .filter(({ id, node }) => {
          if (!node) {
            console.warn(`No node found with id: ${id}`);
            return false;
          }
          const shape = pages[activePage].shapes.find(
            (s) => s.id.toString() === node.id()
          );
          return shape && !shape.locked;
        })
        .map(({ node }) => node);

      transformerRef.current.nodes(selectedNodes);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [
    selectedShapeIds,
    pages,
    activePage,
    selectedTool,
    isEditMode,
    stageRef,
    transformerRef,
    imageLoadCounter,
    isExporting,
  ]);  

  useEffect(() => {
    const transformer = measurementRulerTransformerRef.current;
    const rulerNode = measurementRulerRef.current;

    if (!transformer) return;

    if (
      isMeasurementRulerVisible &&
      isMeasurementRulerSelected &&
      rulerNode &&
      !isExporting
    ) {
      transformer.nodes([rulerNode]);
    } else {
      transformer.nodes([]);
    }

    const layer = transformer.getLayer();

    if (layer) {
      layer.batchDraw();
    }
  }, [
    isMeasurementRulerVisible,
    isMeasurementRulerSelected,
    isExporting,
    measurementRulerSettings.unit,
  ]);

  useEffect(() => {
    if (!isMeasurementRulerVisible) {
      setIsMeasurementRulerSelected(false);
    }
  }, [isMeasurementRulerVisible]);
  
  // Get all fonts used on this page
    const fontsUsed = Array.from(
      new Set(
        pages[activePage].shapes
          .filter(
            (shape) =>
              (shape.type === 'text' && shape.fontFamily) ||
              (shape.type === 'wordSearch' && shape.wordSearchFontFamily)
          )
          .map((shape) =>
            shape.type === 'wordSearch'
              ? shape.wordSearchFontFamily
              : shape.fontFamily
          )
          .filter(Boolean)
      )
    );

  // Load every app font once, not only the fonts currently visible on the page.
  // This prevents first-change lag and default-font flashes.
  const fontsReady = useFontLoader(APP_FONT_FAMILIES);

  useEffect(() => {
    if (!fontsReady) return;

    const stage = stageRef.current;
    if (!stage) return;

    requestAnimationFrame(() => {
      stage.batchDraw();
    });
  }, [fontsReady, pages, activePage, stageRef]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const cursorStyle = getBaseCursorStyle();
    const container = stage.container();

    container.style.cursor = cursorStyle;

    const canvases = container.querySelectorAll('canvas');
    canvases.forEach((canvas) => {
      canvas.style.cursor = cursorStyle;
    });
  }, [selectedTool, isDragging, isMiddleGrabbing]);

  useLayoutEffect(() => {
    if (!isEditingText || !textEditorRef.current || !textMeasureRef.current) return;

    const editor = textEditorRef.current;
    const measure = textMeasureRef.current;

    const text = editor.innerText || '';
    measure.innerText = text || ' ';

    const rect = measure.getBoundingClientRect();

    editor.style.width = `${Math.max(2, Math.ceil(rect.width))}px`;
    editor.style.height = `${Math.max(editingTextPosition.fontSize, Math.ceil(rect.height))}px`;
  }, [isEditingText, editingTextValue, editingTextPosition]);

  // Don’t render until fonts are loaded
  if (!fontsReady) return null;

  if (!pages?.[activePage]?.shapes) {
    return null;
  }
 
  const generateSimpleGridLines = (width, height, gridSize = 50) => {
    const lines = [];
    for (let i = 0; i <= width; i += gridSize) {
      lines.push(
        <Line
          key={`simple-v-${i}`}
          points={[i, 0, i, height]}
          stroke="#e0e0e0"
          strokeWidth={1}
        />
      );
    }
    for (let j = 0; j <= height; j += gridSize) {
      lines.push(
        <Line
          key={`simple-h-${j}`}
          points={[0, j, width, j]}
          stroke="#e0e0e0"
          strokeWidth={1}
        />
      );
    }
    return lines;
  };

  const generateQuartersGridLines = (width, height) => {
    const lines = [];
    const quarterWidth = width / 4;
    const quarterHeight = height / 4;

    for (let i = 1; i <= 3; i++) {
      const x = quarterWidth * i;
      lines.push(
        <Line
          key={`quarters-v-${i}`}
          points={[x, 0, x, height]}
          stroke="#ff0000"
          strokeWidth={2}
        />
      );
    }

    for (let j = 1; j <= 3; j++) {
      const y = quarterHeight * j;
      lines.push(
        <Line
          key={`quarters-h-${j}`}
          points={[0, y, width, y]}
          stroke="#ff0000"
          strokeWidth={2}
        />
      );
    }

    return lines;
  };

  const generateThirdsGridLines = (width, height) => {
    const lines = [];
    const thirdWidth = width / 3;
    const thirdHeight = height / 3;

    for (let i = 1; i <= 2; i++) {
      const x = thirdWidth * i;
      lines.push(
        <Line
          key={`thirds-v-${i}`}
          points={[x, 0, x, height]}
          stroke="#0000ff"
          strokeWidth={2}
        />
      );
    }

    for (let j = 1; j <= 2; j++) {
      const y = thirdHeight * j;
      lines.push(
        <Line
          key={`thirds-h-${j}`}
          points={[0, y, width, y]}
          stroke="#0000ff"
          strokeWidth={2}
        />
      );
    }

    return lines;
  };

  const generateCustomGridLines = (width, height, horizontalSections = 4, verticalSections = 4) => {
  const lines = [];

  const safeHorizontalSections = Math.max(1, parseInt(horizontalSections, 10) || 1);
  const safeVerticalSections = Math.max(1, parseInt(verticalSections, 10) || 1);

  const rowHeight = height / safeHorizontalSections;
  const columnWidth = width / safeVerticalSections;

  // Horizontal lines
  for (let i = 1; i < safeHorizontalSections; i++) {
    const y = rowHeight * i;
    lines.push(
      <Line
        key={`custom-h-${i}`}
        points={[0, y, width, y]}
        stroke="#22aa66"
        strokeWidth={2}
        listening={false}
      />
    );
  }

  // Vertical lines
  for (let i = 1; i < safeVerticalSections; i++) {
    const x = columnWidth * i;
    lines.push(
      <Line
        key={`custom-v-${i}`}
        points={[x, 0, x, height]}
        stroke="#22aa66"
        strokeWidth={2}
        listening={false}
      />
    );
  }

  return lines;
};

    const generateRulerGrid = (width, height) => {
  const lines = [];
  const labels = [];

  const pxPerCm = 37.8;
  const pxPerMm = pxPerCm / 10;

  // Show vertical lines and top labels (ruler on top)
  const cmCountX = Math.floor(width / pxPerCm);
  for (let cm = 0; cm <= cmCountX; cm++) {
    const x = Math.round(cm * pxPerCm);
    lines.push(
      <Line
        key={`ruler-v-cm-${cm}`}
        points={[x, 0, x, height]}
        stroke="#ccc"
        strokeWidth={1}
        listening={false}
      />
    );
    labels.push(
      <Text
        key={`label-top-${cm}`}
        text={`${cm}`}
        x={x + 2}
        y={2}
        fontSize={10}
        fill="#666"
        listening={false}
      />
    );
  }

  // Faint mm lines in between
  for (let mm = 0; mm <= width / pxPerMm; mm++) {
    const x = Math.round(mm * pxPerMm);
    if (x % Math.round(pxPerCm) !== 0) {
      lines.push(
        <Line
          key={`ruler-v-mm-${mm}`}
          points={[x, 0, x, height]}
          stroke="#ddd"
          strokeWidth={0.5}
          listening={false}
        />
      );
    }
  }

  // Show horizontal lines and left labels (ruler on left)
  const cmCountY = Math.floor(height / pxPerCm);
  for (let cm = 0; cm <= cmCountY; cm++) {
    const y = Math.round(cm * pxPerCm);
    lines.push(
      <Line
        key={`ruler-h-cm-${cm}`}
        points={[0, y, width, y]}
        stroke="#ccc"
        strokeWidth={1}
        listening={false}
      />
    );
    labels.push(
      <Text
        key={`label-left-${cm}`}
        text={`${cm}`}
        x={2}
        y={y + 2}
        fontSize={10}
        fill="#666"
        listening={false}
      />
    );
  }

  // Faint mm lines in between
  for (let mm = 0; mm <= height / pxPerMm; mm++) {
    const y = Math.round(mm * pxPerMm);
    if (y % Math.round(pxPerCm) !== 0) {
      lines.push(
        <Line
          key={`ruler-h-mm-${mm}`}
          points={[0, y, width, y]}
          stroke="#ddd"
          strokeWidth={0.5}
          listening={false}
        />
      );
    }
  }

  return [...lines, ...labels];
};

  const renderMeasurementRuler = () => {
    const PX_PER_CM = 37.8;
    const PX_PER_INCH = PX_PER_CM * 2.54;

    const rulerLengthCm = 30;
    const measuredWidth = rulerLengthCm * PX_PER_CM;

    // Extra physical ruler body beyond the 0 cm and 30 cm marks.
    const rulerEndPadding = 10;

    const rulerWidth = measuredWidth + rulerEndPadding * 2;
    const rulerHeight = 74;

    const unit = measurementRulerSettings?.unit === 'in' ? 'in' : 'cm';
    const ticks = [];
    const labels = [];

    if (unit === 'cm') {
      const totalMillimetres = rulerLengthCm * 10;

      for (let mm = 0; mm <= totalMillimetres; mm++) {
        const x = (mm / 10) * PX_PER_CM;
        const isCentimetre = mm % 10 === 0;
        const isHalfCentimetre = mm % 5 === 0;

        const tickHeight = isCentimetre
          ? 30
          : isHalfCentimetre
            ? 21
            : 13;

        ticks.push(
          <Line
            key={`measurement-ruler-cm-tick-${mm}`}
            points={[
              -rulerWidth / 2 + rulerEndPadding + x,
              -rulerHeight / 2,
              -rulerWidth / 2 + rulerEndPadding + x,
              -rulerHeight / 2 + tickHeight,
            ]}
            stroke="#3f3528"
            strokeWidth={isCentimetre ? 1.5 : 1}
            listening={false}
          />
        );

        if (isCentimetre) {
          labels.push(
            <Text
              key={`measurement-ruler-cm-label-${mm}`}
              text={`${mm / 10}`}
              x={-rulerWidth / 2 + rulerEndPadding + x - 10}
              y={-rulerHeight / 2 + 34}
              width={20}
              align="center"
              fontSize={12}
              fontFamily="Arial"
              fill="#3f3528"
              listening={false}
            />
          );
        }
      }
    } else {
      const eighthInchWidth = PX_PER_INCH / 8;
      const totalEighths = Math.floor(measuredWidth / eighthInchWidth);

      for (let eighth = 0; eighth <= totalEighths; eighth++) {
        const x = eighth * eighthInchWidth;
        const isWholeInch = eighth % 8 === 0;
        const isHalfInch = eighth % 4 === 0;
        const isQuarterInch = eighth % 2 === 0;

        const tickHeight = isWholeInch
          ? 30
          : isHalfInch
            ? 24
            : isQuarterInch
              ? 18
              : 12;

        ticks.push(
          <Line
            key={`measurement-ruler-inch-tick-${eighth}`}
            points={[
                -rulerWidth / 2 + rulerEndPadding + x,
                -rulerHeight / 2,
                -rulerWidth / 2 + rulerEndPadding + x,
                -rulerHeight / 2 + tickHeight,
              ]}
            stroke="#3f3528"
            strokeWidth={isWholeInch ? 1.5 : 1}
            listening={false}
          />
        );

        if (isWholeInch) {
          labels.push(
            <Text
              key={`measurement-ruler-inch-label-${eighth}`}
              text={`${eighth / 8}`}
              x={-rulerWidth / 2 + rulerEndPadding + x - 10}
              y={-rulerHeight / 2 + 34}
              width={20}
              align="center"
              fontSize={12}
              fontFamily="Arial"
              fill="#3f3528"
              listening={false}
            />
          );
        }
      }
    }

    return (
      <Group
        ref={measurementRulerRef}
        id="measurement-ruler"
        name="measurement-ruler"
        x={measurementRulerSettings.x}
        y={measurementRulerSettings.y}
        rotation={measurementRulerSettings.rotation || 0}
        draggable={selectedTool === 'select'}
        onMouseDown={(e) => {
          if (e?.evt?.button === 1) {
            e.cancelBubble = true;
            return;
          }

          e.cancelBubble = true;

          setIsMeasurementRulerSelected(true);
          setSelectedShapeIds([]);
          clearAllWordSearchSelections();

          if (selectedTool !== 'select') {
            setSelectedTool('select');
          }
        }}
        onTouchStart={(e) => {
          e.cancelBubble = true;

          setIsMeasurementRulerSelected(true);
          setSelectedShapeIds([]);
          clearAllWordSearchSelections();
        }}
        onDragEnd={(e) => {
          setMeasurementRulerSettings((prev) => ({
            ...prev,
            x: e.target.x(),
            y: e.target.y(),
          }));
        }}
        onTransformEnd={(e) => {
          const node = e.target;

          setMeasurementRulerSettings((prev) => ({
            ...prev,
            x: node.x(),
            y: node.y(),
            rotation: node.rotation(),
          }));

          // The ruler must never retain scaling.
          node.scaleX(1);
          node.scaleY(1);
        }}
      >
        <Rect
          x={-rulerWidth / 2}
          y={-rulerHeight / 2}
          width={rulerWidth}
          height={rulerHeight}
          fill="rgba(244, 208, 111, 0.82)"
          stroke="#6b5735"
          strokeWidth={2}
          cornerRadius={5}
          shadowColor="rgba(0,0,0,0.25)"
          shadowBlur={5}
          shadowOffsetX={2}
          shadowOffsetY={3}
        />

        <Line
          points={[
            -rulerWidth / 2,
            rulerHeight / 2 - 8,
            rulerWidth / 2,
            rulerHeight / 2 - 8,
          ]}
          stroke="rgba(107, 87, 53, 0.35)"
          strokeWidth={1}
          listening={false}
        />

        {ticks}
        {labels}

        <Text
          text={unit === 'cm' ? '30 cm' : 'inches'}
          x={-42}
          y={rulerHeight / 2 - 21}
          width={84}
          align="center"
          fontSize={10}
          fontFamily="Arial"
          fill="rgba(63,53,40,0.7)"
          listening={false}
        />
      </Group>
    );
  };

   const renderGridShape = (shape, commonProps) => {
      const width = shape.width || 1;
      const height = shape.height || 1;
      const rows = Math.max(1, parseInt(shape.gridRows, 10) || 1);
      const columns = Math.max(1, parseInt(shape.gridColumns, 10) || 1);

      const cellWidth = width / columns;
      const cellHeight = height / rows;

      const strokeColor =
        shape.strokeEnabled !== false
          ? (shape.strokeColor || '#000000')
          : 'transparent';

      const strokeWidth = shape.strokeWidth || 2;
      const dash = shape.dashEnabled ? shape.dash : [];
      const isDotted = shape.strokeStyle === 'dotted';
      const dottedLineProps = {
        lineCap: isDotted ? 'round' : 'square',
        lineJoin: isDotted ? 'round' : 'miter',
      };
      const defaultCellFill = shape.fillColor || '#ffffff';
      const cellFills = Array.isArray(shape.gridCellFills)
        ? shape.gridCellFills
        : [];

      const cellRects = [];
      const lines = [];

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < columns; col++) {
          const cellIndex = row * columns + col;
          const x = col * cellWidth;
          const y = row * cellHeight;

          cellRects.push(
            <Rect
              key={`${shape.id}-cell-${cellIndex}`}
              x={x}
              y={y}
              width={cellWidth}
              height={cellHeight}
              fill={
                shape.fillEnabled
                  ? (cellFills[cellIndex] || defaultCellFill)
                  : 'rgba(0,0,0,0.001)'
              }
              {...(shape.fillEnabled && (shape.fillStyle || 'solid') !== 'solid'
                ? getShapeFillProps({
                    ...shape,
                    width: cellWidth,
                    height: cellHeight,
                    fillColor: cellFills[cellIndex] || defaultCellFill,
                  })
                : {})}
              stroke="transparent"
              listening={!shape.locked}
              onMouseDown={(e) => {
                if (selectedTool === 'bucket') {
                  e.cancelBubble = true;

                  bucketDraggingRef.current = true;

                  const cellKey = `${shape.id}-${cellIndex}`;
                  bucketPaintedCellsRef.current.clear();
                  bucketPaintedCellsRef.current.add(cellKey);

                  if (applyBucketToGridCell) {
                    applyBucketToGridCell(shape.id.toString(), cellIndex, {
                      skipUndo: false,
                    });
                  }
                }
              }}
              onMouseEnter={(e) => {
                if (selectedTool !== 'bucket') return;
                if (!bucketDraggingRef.current) return;

                const cellKey = `${shape.id}-${cellIndex}`;
                if (bucketPaintedCellsRef.current.has(cellKey)) return;

                bucketPaintedCellsRef.current.add(cellKey);

                if (applyBucketToGridCell) {
                  applyBucketToGridCell(shape.id.toString(), cellIndex, {
                    skipUndo: true,
                  });
                }
              }}
              onClick={(e) => {
                if (selectedTool !== 'bucket') {
                  handleShapeClick(e, shape.id.toString());
                }
              }}
              onTouchStart={(e) => {
                if (selectedTool === 'bucket') {
                  if (applyBucketToGridCell) {
                    applyBucketToGridCell(shape.id.toString(), cellIndex, {
                      skipUndo: false,
                    });
                  }
                  return;
                }

                handleShapeClick(e, shape.id.toString());
              }}
            />
          );
        }
      }

      for (let row = 1; row < rows; row++) {
        const y = (height / rows) * row;
        lines.push(
          <Line
            key={`${shape.id}-row-${row}`}
            points={[0, y, width, y]}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            dash={dash}
            {...dottedLineProps}
            strokeScaleEnabled={false}
            listening={false}
          />
        );
      }

      for (let col = 1; col < columns; col++) {
        const x = (width / columns) * col;
        lines.push(
          <Line
            key={`${shape.id}-col-${col}`}
            points={[x, 0, x, height]}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            dash={dash}
            {...dottedLineProps}
            strokeScaleEnabled={false}
            listening={false}
          />
        );
      }

      const gridGroupProps = {
        ...commonProps,
        onMouseDown: (e) => {
          if (e.evt.button === 1) {
            e.cancelBubble = true;
            e.target.stopDrag();
            return;
          }

          if (selectedTool === 'bucket') {
            return;
          }

          if (shape.type !== 'wordSearch') {
            clearAllWordSearchSelections();
          }

          handleShapeClick(e, shape.id.toString());
        },
        onTouchStart: (e) => {
          if (selectedTool === 'bucket') {
            const groupNode = e.target;
            const localPos = groupNode.getRelativePointerPosition?.();

            if (localPos && applyBucketToGridCell) {
              const col = Math.min(
                columns - 1,
                Math.max(0, Math.floor(localPos.x / cellWidth))
              );
              const row = Math.min(
                rows - 1,
                Math.max(0, Math.floor(localPos.y / cellHeight))
              );

              const cellIndex = row * columns + col;
              applyBucketToGridCell(shape.id.toString(), cellIndex);
            }

            return;
          }

          handleShapeClick(e, shape.id.toString());
        },
      };

      return (
        <Group {...gridGroupProps}>
          {cellRects}

          <Rect
            width={width}
            height={height}
            fill="transparent"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            dash={dash}
            {...dottedLineProps}
            strokeScaleEnabled={false}
            listening={false}
          />

          {lines}
        </Group>
      );
    };

    const renderWordSearchShape = (shape, commonProps) => {
    const width = Math.max(1, shape.width || 1);
    const height = Math.max(1, shape.height || 1);
    const rows = Math.max(1, parseInt(shape.wordSearchRows, 10) || 1);
    const columns = Math.max(1, parseInt(shape.wordSearchColumns, 10) || 1);
    const cellWidth = width / columns;
    const cellHeight = height / rows;

    const strokeColor =
      shape.strokeEnabled !== false
        ? (shape.strokeColor || '#000000')
        : 'transparent';

    const strokeWidth = shape.strokeWidth || 2;
    const dash = shape.dashEnabled ? shape.dash : [];
    const isDotted = shape.strokeStyle === 'dotted';
    const dottedLineProps = {
      lineCap: isDotted ? 'round' : 'square',
      lineJoin: isDotted ? 'round' : 'miter',
    };
    const letters = Array.isArray(shape.wordSearchLetters) ? shape.wordSearchLetters : [];    
    const selectedCellIndex = shape.wordSearchSelectedCellIndex ?? null;

    const lines = [];
    const cellOverlays = [];
    const textNodes = [];

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const cellIndex = row * columns + col;
        const x = col * cellWidth;
        const y = row * cellHeight;
        const rawLetter = letters[cellIndex] || '';
        const displayLetter = rawLetter;

        const cellInsetX = Math.min(cellWidth * 0.18, 10);
        const cellInsetY = Math.min(cellHeight * 0.18, 10);
        const innerCellX = x + cellInsetX;
        const innerCellY = y + cellInsetY;
        const innerCellWidth = Math.max(1, cellWidth - cellInsetX * 2);
        const innerCellHeight = Math.max(1, cellHeight - cellInsetY * 2);

        cellOverlays.push(
          <Rect
            key={`${shape.id}-cell-hit-${cellIndex}`}
            x={innerCellX}
            y={innerCellY}
            width={innerCellWidth}
            height={innerCellHeight}
            fill={
              !isExporting && selectedCellIndex === cellIndex
                ? 'rgba(59,130,246,0.16)'
                : 'rgba(0,0,0,0.001)'
            }
            stroke="transparent"
            listening={!shape.locked}
            onMouseDown={(e) => {
              e.cancelBubble = true;
              handleShapeClick(e, shape.id.toString());

              if (shape.wordSearchPaintTextEnabled && paintWordSearchCellText) {
                const cellKey = `${shape.id}-${cellIndex}`;
                wordSearchTextDraggingRef.current = true;
                wordSearchTextPaintedCellsRef.current.clear();
                wordSearchTextPaintedCellsRef.current.add(cellKey);

                paintWordSearchCellText(
                  shape.id.toString(),
                  cellIndex,
                  shape.wordSearchPaintTextValue || '',
                  { skipUndo: false }
                );
                return;
              }

              if (selectWordSearchCell) {
                selectWordSearchCell(shape.id.toString(), cellIndex);
              }
            }}
            onMouseEnter={() => {
              if (!shape.wordSearchPaintTextEnabled) return;
              if (!wordSearchTextDraggingRef.current) return;
              if (!paintWordSearchCellText) return;

              const cellKey = `${shape.id}-${cellIndex}`;
              if (wordSearchTextPaintedCellsRef.current.has(cellKey)) return;

              wordSearchTextPaintedCellsRef.current.add(cellKey);

              paintWordSearchCellText(
                shape.id.toString(),
                cellIndex,
                shape.wordSearchPaintTextValue || '',
                { skipUndo: true }
              );
            }}
            onTouchStart={(e) => {
              e.cancelBubble = true;
              handleShapeClick(e, shape.id.toString());

              if (shape.wordSearchPaintTextEnabled && paintWordSearchCellText) {
                paintWordSearchCellText(
                  shape.id.toString(),
                  cellIndex,
                  shape.wordSearchPaintTextValue || '',
                  { skipUndo: false }
                );
                return;
              }

              if (selectWordSearchCell) {
                selectWordSearchCell(shape.id.toString(), cellIndex);
              }
            }}
          />
        );

        textNodes.push(
          <Text
            key={`${shape.id}-letter-${cellIndex}`}
            x={x}
            y={y}
            width={cellWidth}
            height={cellHeight}
            text={displayLetter}
            align="center"
            verticalAlign="middle"
            fontSize={Math.min(shape.wordSearchFontSize || 24, cellHeight * 0.8, cellWidth * 0.8)}
            fontFamily={shape.wordSearchFontFamily || 'Arial'}
            fill="#000000"
            listening={false}
          />
        );
      }
    }

    for (let row = 1; row < rows; row++) {
      const y = (height / rows) * row;
      lines.push(
        <Line
          key={`${shape.id}-row-${row}`}
          points={[0, y, width, y]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          {...dottedLineProps}
          strokeScaleEnabled={false}
          listening={!shape.locked}
          hitStrokeWidth={Math.max(12, strokeWidth + 8)}
          onClick={(e) => {
            handleShapeClick(e, shape.id.toString());
            if (clearWordSearchCellSelection) {
              clearWordSearchCellSelection(shape.id.toString());
            }
          }}
        />
      );
    }

    for (let col = 1; col < columns; col++) {
      const x = (width / columns) * col;
      lines.push(
        <Line
          key={`${shape.id}-col-${col}`}
          points={[x, 0, x, height]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          {...dottedLineProps}
          strokeScaleEnabled={false}
          listening={!shape.locked}
          hitStrokeWidth={Math.max(12, strokeWidth + 8)}
          onClick={(e) => {
            handleShapeClick(e, shape.id.toString());
            if (clearWordSearchCellSelection) {
              clearWordSearchCellSelection(shape.id.toString());
            }
          }}
        />
      );
    }

    return (
      <Group {...commonProps}>
        <Rect
          width={width}
          height={height}
          fill={shape.fillEnabled ? (shape.fillColor || '#ffffff') : 'rgba(0,0,0,0.001)'}
          {...(shape.fillEnabled ? getShapeFillProps(shape) : {})}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          {...dottedLineProps}
          strokeScaleEnabled={false}
          listening={!shape.locked}
          onClick={(e) => {
            handleShapeClick(e, shape.id.toString());
            if (clearWordSearchCellSelection) {
              clearWordSearchCellSelection(shape.id.toString());
            }
          }}
        />
        {cellOverlays}
        {lines}
        {textNodes}
      </Group>
    );
  };

    const renderMazeShape = (shape, commonProps) => {
      const width = Math.max(1, shape.width || 1);
      const height = Math.max(1, shape.height || 1);

      const strokeColor =
        shape.strokeEnabled !== false
          ? (shape.strokeColor || '#000000')
          : 'transparent';

      const strokeWidth = shape.strokeWidth || 2;
      const dash = shape.dashEnabled ? shape.dash : [];
      const walls = Array.isArray(shape.mazeWalls) ? shape.mazeWalls : [];

      return (
        <Group {...commonProps}>
          <Rect
            width={width}
            height={height}
            fill={shape.fillEnabled ? (shape.fillColor || '#ffffff') : 'rgba(0,0,0,0.001)'}
            {...(shape.fillEnabled ? getShapeFillProps(shape) : {})}
            stroke="transparent"
            strokeWidth={0}
            strokeScaleEnabled={false}
          />

          {walls.map((wall, index) => (
            <Line
              key={`${shape.id}-maze-wall-${index}`}
              points={wall}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              dash={dash}
              strokeScaleEnabled={false}
              listening={false}
              lineCap="square"
              lineJoin="miter"
            />
          ))}
        </Group>
      );
    };

        const renderCircleSegmentsShape = (shape, commonProps) => {
          const radius = Math.max(1, shape.radius || 1);
          const segments = Math.max(1, parseInt(shape.segments, 10) || 1);

          const strokeColor =
            shape.strokeEnabled !== false
              ? (shape.strokeColor || '#000000')
              : 'transparent';

          const strokeWidth = shape.strokeWidth || 2;
          const dash = shape.dashEnabled ? shape.dash : [];
          const isDotted = shape.strokeStyle === 'dotted';
          const dottedLineProps = {
            lineCap: isDotted ? 'round' : 'square',
            lineJoin: isDotted ? 'round' : 'miter',
          };
          const defaultSegmentFill = shape.fillColor || '#ffffff';
          const segmentFills = Array.isArray(shape.circleSegmentFills)
            ? shape.circleSegmentFills
            : [];

          const segmentAngle = 360 / segments;
          const wedges = [];
          const lines = [];

          for (let i = 0; i < segments; i++) {
            const fillColor = shape.fillEnabled
              ? (segmentFills[i] || defaultSegmentFill)
              : 'rgba(0,0,0,0.001)';

            wedges.push(
              <Wedge
                key={`${shape.id}-segment-fill-${i}`}
                x={0}
                y={0}
                radius={radius}
                angle={segmentAngle}
                rotation={-90 + i * segmentAngle}
                fill={fillColor}
                {...(shape.fillEnabled && (shape.fillStyle || 'solid') !== 'solid'
                  ? getShapeFillProps({ ...shape, fillColor })
                  : {})}
                stroke="transparent"
                listening={!shape.locked}
                onMouseDown={(e) => {
                  if (selectedTool === 'bucket') {
                    e.cancelBubble = true;

                    if (applyBucketToCircleSegment) {
                      applyBucketToCircleSegment(shape.id.toString(), i);
                    }
                  }
                }}
                onClick={(e) => {
                  if (selectedTool !== 'bucket') {
                    handleShapeClick(e, shape.id.toString());
                  }
                }}
                onTouchStart={(e) => {
                  if (selectedTool === 'bucket') {
                    if (applyBucketToCircleSegment) {
                      applyBucketToCircleSegment(shape.id.toString(), i);
                    }
                    return;
                  }

                  handleShapeClick(e, shape.id.toString());
                }}
              />
            );
          }

      for (let i = 0; i < segments; i++) {
        const angle = (Math.PI * 2 * i) / segments - Math.PI / 2;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;

        lines.push(
          <Line
            key={`${shape.id}-segment-line-${i}`}
            points={[0, 0, x, y]}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            dash={dash}
            {...dottedLineProps}
            strokeScaleEnabled={false}
            listening={false}
          />
        );
      }

      const circleSegmentGroupProps = {
        ...commonProps,
        onMouseDown: (e) => {
          if (e.evt.button === 1) {
            e.cancelBubble = true;
            e.target.stopDrag();
            return;
          }

          if (selectedTool === 'bucket') {
            return;
          }

          if (shape.type !== 'wordSearch') {
            clearAllWordSearchSelections();
          }

          handleShapeClick(e, shape.id.toString());
        },
      };

      return (
        <Group {...circleSegmentGroupProps}>
          {wedges}

          <Circle
            radius={radius}
            fill="transparent"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            dash={dash}
            {...dottedLineProps}
            strokeScaleEnabled={false}
            listening={false}
          />

          {lines}
        </Group>
      );
    };

    const crosshairTools = ['shape', 'line'];

    const getBaseCursorStyle = () => {
      if (isMiddleGrabbing) {
        return `url(${cursorHandGrab}) 15 15, grabbing`;
      }

      if (selectedTool === 'hand') {
        return isDragging
          ? `url(${cursorHandGrab}) 15 15, grabbing`
          : `url(${cursorHandOpen}) 15 15, grab`;
      }

      if (selectedTool === 'pen') {
        return `url(${cursorPen}) 1 30, crosshair`;
      }

      if (selectedTool === 'pencil') {
        return `url(${cursorPencil}) 1 30, crosshair`;
      }

      if (selectedTool === 'text') {
        return `url(${cursorText}) 0 0, text`;
      }

      if (crosshairTools.includes(selectedTool)) {
        return `url(${cursorCrosshair}) 15 15, crosshair`;
      }

      if (selectedTool === 'bucket') {
        return `url(${cursorPaintBucket}) 1 30, pointer`;
      }

      return `url(${cursorDefault}) 2 2, default`;
    };

  const getTransformCursor = (anchorName) => {
      switch (anchorName) {
        case 'rotater':
          return `url(${cursorRotate}) 15 15, grab`;

        case 'top-center':
        case 'bottom-center':
          return `url(${cursorResizeVertical}) 15 15, ns-resize`;

        case 'middle-left':
        case 'middle-right':
          return `url(${cursorResizeHorizontal}) 15 15, ew-resize`;

        case 'top-left':
        case 'bottom-right':
          return `url(${cursorResizeDiagonal2}) 15 15, nwse-resize`;

        case 'top-right':
        case 'bottom-left':
          return `url(${cursorResizeDiagonal1}) 15 15, nesw-resize`;

        default:
          return getBaseCursorStyle();
      }
    };
  
  
  const getAnchorKey = (anchorNode) => {
    if (!anchorNode || typeof anchorNode.name !== 'function') return '';

    const rawName = anchorNode.name() || '';

    if (rawName.includes('rotater')) return 'rotater';
    if (rawName.includes('top-left')) return 'top-left';
    if (rawName.includes('top-center')) return 'top-center';
    if (rawName.includes('top-right')) return 'top-right';
    if (rawName.includes('middle-left')) return 'middle-left';
    if (rawName.includes('middle-right')) return 'middle-right';
    if (rawName.includes('bottom-left')) return 'bottom-left';
    if (rawName.includes('bottom-center')) return 'bottom-center';
    if (rawName.includes('bottom-right')) return 'bottom-right';

    return '';
  };

    const setStageCursor = (cursorStyle) => {
      const stage = stageRef.current;
      if (!stage) return;

      const container = stage.container();
      container.style.cursor = cursorStyle;

      const canvases = container.querySelectorAll('canvas');
      canvases.forEach((canvas) => {
        canvas.style.cursor = cursorStyle;
      });
    };

    
  const selectedShapes = selectedShapeIds.map((id) =>
    pages[activePage].shapes.find((shape) => shape.id.toString() === id)
  );

  const includesLine = selectedShapes.some(
    (shape) => shape && shape.type === 'line'
  );

  const transformerProps = {};

    if (includesLine) {
      transformerProps.ignoreStroke = false;
    } else {
      transformerProps.ignoreStroke = true;
    }

    // ✅ Dynamic transformer behaviour for tiny shapes
    const getSelectedShapeVisualSize = () => {
      const activeShapes = selectedShapes.filter(Boolean);

      if (activeShapes.length === 0) {
        return { width: 100, height: 100 };
      }

      if (activeShapes.length > 1) {
        const stage = stageRef.current;

        if (!stage) {
          return { width: 100, height: 100 };
        }

        const selectedNodes = selectedShapeIds
          .map((id) => stage.findOne(`#${id}`))
          .filter(Boolean);

        if (selectedNodes.length === 0) {
          return { width: 100, height: 100 };
        }

        const boxes = selectedNodes.map((node) =>
          node.getClientRect({
            relativeTo: stage,
            skipShadow: true,
            skipStroke: false,
          })
        );

        const minX = Math.min(...boxes.map((box) => box.x));
        const minY = Math.min(...boxes.map((box) => box.y));
        const maxX = Math.max(...boxes.map((box) => box.x + box.width));
        const maxY = Math.max(...boxes.map((box) => box.y + box.height));

        return {
          width: Math.max(1, maxX - minX),
          height: Math.max(1, maxY - minY),
        };
      }

      const shape = activeShapes[0];

      const scaleX = Math.abs(shape.scaleX || 1);
      const scaleY = Math.abs(shape.scaleY || 1);

      if (shape.type === 'circle' || shape.type === 'semiCircle' || shape.type === 'circleSegments') {
        const diameter = (shape.radius || 1) * 2;
        return {
          width: diameter * scaleX,
          height: diameter * scaleY,
        };
      }

      if (['triangle', 'pentagon', 'hexagon', 'octagon'].includes(shape.type)) {
        const diameter = (shape.radius || 1) * 2;
        return {
          width: diameter * scaleX,
          height: diameter * scaleY,
        };
      }

      if (shape.type === 'star') {
        const diameter = (shape.outerRadius || 1) * 2;
        return {
          width: diameter * scaleX,
          height: diameter * scaleY,
        };
      }

            if (shape.type === 'line' || shape.type === 'pencil' || shape.type === 'pen') {
              const getBoundsFromFlatPoints = (points = []) => {
                const xs = [];
                const ys = [];

                for (let i = 0; i < points.length; i += 2) {
                  xs.push(points[i]);
                  ys.push(points[i + 1]);
                }

                if (xs.length === 0 || ys.length === 0) {
                  return { width: 100, height: 100 };
                }

                return {
                  width: Math.max(1, Math.max(...xs) - Math.min(...xs)),
                  height: Math.max(1, Math.max(...ys) - Math.min(...ys)),
                };
              };

              const getBoundsFromBezierPoints = (bezierPoints = []) => {
                const allPoints = [];

                bezierPoints.forEach((point) => {
                  allPoints.push(
                    { x: point.x, y: point.y },
                    { x: point.x + point.in.x, y: point.y + point.in.y },
                    { x: point.x + point.out.x, y: point.y + point.out.y }
                  );
                });

                if (allPoints.length === 0) {
                  return { width: 100, height: 100 };
                }

                const minX = Math.min(...allPoints.map((point) => point.x));
                const minY = Math.min(...allPoints.map((point) => point.y));
                const maxX = Math.max(...allPoints.map((point) => point.x));
                const maxY = Math.max(...allPoints.map((point) => point.y));

                return {
                  width: Math.max(1, maxX - minX),
                  height: Math.max(1, maxY - minY),
                };
              };

              const bounds =
                shape.bezierEnabled && Array.isArray(shape.bezierPoints)
                  ? getBoundsFromBezierPoints(shape.bezierPoints)
                  : getBoundsFromFlatPoints(shape.points);

              return {
                width: Math.max(1, bounds.width * scaleX),
                height: Math.max(1, bounds.height * scaleY),
              };
            }

      return {
        width: Math.abs((shape.width || 1) * scaleX),
        height: Math.abs((shape.height || 1) * scaleY),
      };
    };

    const selectedVisualSize = getSelectedShapeVisualSize();
    const smallestSelectedSide = Math.min(
      selectedVisualSize.width,
      selectedVisualSize.height
    );

    const dynamicAnchorSize =
      smallestSelectedSide <= 20
        ? 5
        : smallestSelectedSide <= 40
          ? 6
          : smallestSelectedSide <= 80
            ? 8
            : 12;

    const dynamicTransformerPadding =
      smallestSelectedSide <= 20
        ? 10
        : smallestSelectedSide <= 40
          ? 8
          : smallestSelectedSide <= 80
            ? 4
            : 0;

    const fullTransformerAnchors = [
      'top-left',
      'top-center',
      'top-right',
      'middle-left',
      'middle-right',
      'bottom-left',
      'bottom-center',
      'bottom-right',
    ];

    const cornerOnlyTransformerAnchors = [
      'top-left',
      'top-right',
      'bottom-left',
      'bottom-right',
    ];

    const dynamicEnabledAnchors =
      !includesLine && selectedShapeIds.length === 1 && smallestSelectedSide <= 40
        ? cornerOnlyTransformerAnchors
        : fullTransformerAnchors;

    // ✅ Dynamic rotate handle position
    // Chooses either the normal rotate side or the opposite side,
    // based on which one has more room inside the canvas.
    const getSelectedTransformerBounds = () => {
      const stage = stageRef.current;

      if (!stage || selectedShapeIds.length === 0) {
        return null;
      }

      const selectedNodes = selectedShapeIds
        .map((id) => stage.findOne(`#${id}`))
        .filter(Boolean);

      if (selectedNodes.length === 0) {
        return null;
      }

      const boxes = selectedNodes.map((node) =>
        node.getClientRect({
          relativeTo: stage,
          skipShadow: true,
          skipStroke: false,
        })
      );

      const minX = Math.min(...boxes.map((box) => box.x));
      const minY = Math.min(...boxes.map((box) => box.y));
      const maxX = Math.max(...boxes.map((box) => box.x + box.width));
      const maxY = Math.max(...boxes.map((box) => box.y + box.height));

      return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        centerX: minX + (maxX - minX) / 2,
        centerY: minY + (maxY - minY) / 2,
      };
    };

    const selectedTransformerBounds = getSelectedTransformerBounds();

    const rotateClearance = dynamicTransformerPadding + 34;
    const rotateEdgeMargin = 45;
    const canvasWidth = pages[activePage].width;
    const canvasHeight = pages[activePage].height;

    const isMultiObjectSelection = selectedShapeIds.length > 1;

    const firstSelectedNode =
      selectedShapeIds.length > 0 && stageRef.current
        ? stageRef.current.findOne(`#${selectedShapeIds[0]}`)
        : null;

    // ✅ Single objects rotate the transformer direction.
    // ✅ Multi-select transformer stays upright, even if selected objects rotate inside it.
    const selectedRotation =
      isMultiObjectSelection
        ? 0
        : firstSelectedNode && typeof firstSelectedNode.rotation === 'function'
          ? firstSelectedNode.rotation()
          : selectedShapes.length === 1 && selectedShapes[0]
            ? selectedShapes[0].rotation || 0
            : 0;

    // Positive rotateAnchorOffset direction.
    // At 0 degrees this points upward.
    // At 90 degrees this points right.
    // At 180 degrees this points downward.
    // At 270 degrees this points left.
    const rotationRadians = (selectedRotation * Math.PI) / 180;
    const rotateDirectionX = Math.sin(rotationRadians);
    const rotateDirectionY = -Math.cos(rotationRadians);

    // ✅ Work from the outside edge of the transformer,
    // not from the centre. This fixes full-size shapes near edges.
    const rotateDirectionHalfSize = selectedTransformerBounds
      ? (
          Math.abs(rotateDirectionX) * selectedTransformerBounds.width / 2 +
          Math.abs(rotateDirectionY) * selectedTransformerBounds.height / 2
        )
      : 0;

    const rotateHandleDistance = rotateDirectionHalfSize + rotateClearance;

    const normalRotatePoint = selectedTransformerBounds
      ? {
          x: selectedTransformerBounds.centerX + rotateDirectionX * rotateHandleDistance,
          y: selectedTransformerBounds.centerY + rotateDirectionY * rotateHandleDistance,
        }
      : null;

    const flippedRotatePoint = selectedTransformerBounds
      ? {
          x: selectedTransformerBounds.centerX - rotateDirectionX * rotateHandleDistance,
          y: selectedTransformerBounds.centerY - rotateDirectionY * rotateHandleDistance,
        }
      : null;

    const getEdgeScore = (point) => {
      if (!point) return -9999;

      return Math.min(
        point.x,
        canvasWidth - point.x,
        point.y,
        canvasHeight - point.y
      );
    };

    const normalScore = getEdgeScore(normalRotatePoint);
    const flippedScore = getEdgeScore(flippedRotatePoint);

    const normalIsTooClose = normalScore < rotateEdgeMargin;
    const flippedHasMoreRoom = flippedScore > normalScore;

    const dynamicRotateAnchorOffset =
      normalIsTooClose && flippedHasMoreRoom
        ? -rotateClearance
        : rotateClearance;

  
  return (
        <div
          className="canvas-wrapper"
          onMouseDown={(e) => {
            // Only deselect when clicking the empty area outside the canvas
            if (e.target !== e.currentTarget) return;

            // Ignore middle mouse / pan clicks
            if (e.button === 1) return;

            // Only do this while using the select tool
            if (selectedTool !== 'select') return;

            setSelectedShapeIds([]);
            setIsMeasurementRulerSelected(false);
            clearAllWordSearchSelections();

            if (transformerRef.current) {
              transformerRef.current.nodes([]);
              const transformerLayer = transformerRef.current.getLayer();

              if (transformerLayer) {
                transformerLayer.batchDraw();
              }
            }
          }}
        >
      <div
        id="canvas-container"
        className="canvas-area"
        style={{
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${scale})`,
          transformOrigin: 'center center',
          backgroundColor: pages[activePage].backgroundColor,
          width: pages[activePage].width,
          height: pages[activePage].height,
          position: 'relative',
          overflow: 'hidden',          
        }}
      >
        <Stage
          width={pages[activePage].width}
          height={pages[activePage].height}
          onMouseDown={handleMouseDownInternal}
          onMouseMove={(e) => {
            handleMouseMoveInternal(e);
          }}
          onMouseUp={(e) => {
            handleMouseUpInternal(e);
          }}
          onMouseEnter={() => {
            const stage = stageRef.current;
            if (!stage) return;

            const container = stage.container();
            const cursorStyle = getBaseCursorStyle();

            container.style.cursor = cursorStyle;

            const canvases = container.querySelectorAll('canvas');
            canvases.forEach((canvas) => {
              canvas.style.cursor = cursorStyle;
            });
          }}
          ref={stageRef}
        >
          {isGridVisible && (
            <Layer>
              {generateSimpleGridLines(
                pages[activePage].width,
                pages[activePage].height
              )}
            </Layer>
          )}      

          {isQuartersGridVisible && (
            <Layer>
              {generateQuartersGridLines(
                pages[activePage].width,
                pages[activePage].height
              )}
            </Layer>
          )}

          {isThirdsGridVisible && (
            <Layer>
              {generateThirdsGridLines(
                pages[activePage].width,
                pages[activePage].height
              )}
            </Layer>
          )}

          {isCustomGridVisible && (
            <Layer>
              {generateCustomGridLines(
                pages[activePage].width,
                pages[activePage].height,
                customGridSettings.horizontalSections,
                customGridSettings.verticalSections
              )}
            </Layer>
          )}

          {isRulerGridVisible && !isExporting && (
            <Layer>
              {generateRulerGrid(pages[activePage].width, pages[activePage].height)}
            </Layer>
          )}
          
          <Layer>
            {pages[activePage].shapes.map((shape) => {
              const isMultipleSelected = selectedShapeIds.length > 1;
              const isShapeLocked = shape.locked;

              const isDraggable =
                !isShapeLocked && selectedTool === 'select';

              const strokeEnabled =
                shape.strokeEnabled !== undefined
                  ? shape.strokeEnabled
                  : true;

              const fillEnabled =
                shape.fillEnabled !== undefined
                  ? shape.fillEnabled
                  : true;

              const fill =
                fillEnabled && shape.type !== 'image'
                  ? shape.fillColor || shape.fill || '#000000'
                  : undefined;

              const strokeOnlyHitEnabled =
                fillEnabled === false &&
                strokeEnabled !== false &&
                [
                  'rect',
                  'roundedRect',
                  'circle',
                  'semiCircle',
                  'triangle',
                  'pentagon',
                  'hexagon',
                  'octagon',
                  'star',
                ].includes(shape.type);

              const stroke =
                strokeEnabled &&
                shape.type !== 'image' &&
                shape.type !== 'text'
                  ? shape.strokeColor || '#000000'
                  : null;

              const commonProps = {
                key: shape.id,
                id: shape.id.toString(),
                x: shape.x || 0,
                y: shape.y || 0,
                ...(fill && { fill }),
                ...(fillEnabled && shape.type !== 'image' ? getShapeFillProps(shape) : {}),
                ...(stroke && { stroke }),
                strokeWidth: shape.strokeWidth,
                rotation: shape.rotation || 0,
                scaleX: shape.scaleX || 1,
                scaleY: shape.scaleY || 1,
                opacity: shape.opacity || 1,
                draggable: isDraggable,
                dash: shape.dashEnabled ? shape.dash : [],
                onMouseDown: (e) => {
                  if (e.evt.button === 1) {
                    e.cancelBubble = true;
                    e.target.stopDrag();
                    return;
                  }

                  if (shape.type !== 'wordSearch') {
                    clearAllWordSearchSelections();
                  }

                  if (selectedTool === 'bucket') {
                    e.cancelBubble = true;
                    applyBucketToShape(shape.id.toString());
                    return;
                  }

                  const stage = e.target.getStage();
                  const pointer = stage?.getPointerPosition();

                  const isStrokeOnlyShape =
                    shape.fillEnabled === false &&
                    shape.strokeEnabled !== false &&
                    [
                      'rect',
                      'roundedRect',
                      'circle',
                      'semiCircle',
                      'triangle',
                      'pentagon',
                      'hexagon',
                      'octagon',
                      'star',
                    ].includes(shape.type);

                  if (
                    selectedTool === 'select' &&
                    isStrokeOnlyShape &&
                    !isPointNearStrokeOnlyShape(shape, pointer, scale)
                  ) {
                    e.cancelBubble = true;
                    e.target.stopDrag();

                    const clickedShapeIndex = pages[activePage].shapes.findIndex(
                      (s) => s.id.toString() === shape.id.toString()
                    );

                    const shapeBelow = findShapeUnderPointer(
                      pages[activePage].shapes.slice(0, clickedShapeIndex),
                      pointer,
                      scale
                    );

                    if (shapeBelow) {
                      handleShapeClick(e, shapeBelow.id.toString());

                      clickThroughDragRef.current = {
                        shapeId: shapeBelow.id.toString(),
                        startX: pointer.x,
                        startY: pointer.y,
                      };
                    } else {
                      setSelectedShapeIds([]);
                      clickThroughDragRef.current = null;

                      // Treat empty no-fill area like clicking the stage,
                      // so drag-select can start underneath it.
                      handleStageMouseDown({
                        ...e,
                        target: stage,
                        passthroughShapeId: shape.id.toString(),
                      });
                    }

                    return;
                  }

                  handleShapeClick(e, shape.id.toString());
                },
                onTouchStart:  (e) => handleShapeClick(e, shape.id.toString()),
                onDblClick:
                  shape.type === 'text'
                    ? () =>
                        handleTextDoubleClick(shape.id.toString())
                    : undefined,
                onTransformEnd: (e) => {
                  const node = e.target;
                  handleTransformEndSnapping(
                    shape.id.toString(),
                    node
                  );
                },

                onTransform: () => {},
                dragBoundFunc: isDraggable
                  ? createDragBoundFunc(shape.id.toString())
                  : undefined,
                onDragStart: isDraggable
                  ? () =>
                      handleDragStartInternal(shape.id.toString())
                  : undefined,
                onDragMove: isDraggable
                  ? () => {}
                  : undefined,
                onDragEnd: isDraggable
                  ? (e) => {
                      handleDragEndInternal(shape.id.toString(), {
                        x: e.target.x(),
                        y: e.target.y(),
                      });
                    }
                  : undefined,
                listening: !(shape.locked && !isEditMode),
                hitStrokeWidth: strokeOnlyHitEnabled
                  ? Math.max(12, (shape.strokeWidth || 2) + 8)
                  : undefined,
              };

            const shapeSpecificProps = {};

              if (
                [
                  'rect',
                  'roundedRect',
                  'circle',
                  'semiCircle',
                  'circleSegments',
                  'triangle',
                  'pentagon',
                  'hexagon',
                  'octagon',
                  'star',
                  'maze',
                  'wordSearch',
                ].includes(shape.type)
              ) {
                shapeSpecificProps.strokeScaleEnabled = false;
              }

              if (shape.type === 'maskedGroup') {
                const [maskShape, contentShape] = shape.children;
                const groupKey = `masked-${shape.id}`;

                const outerX = shape.x || contentShape.x;
                const outerY = shape.y || contentShape.y;
                const outerScaleX = shape.scaleX || 1;
                const outerScaleY = shape.scaleY || 1;
                const outerRotation = shape.rotation || 0;

                const isMaskedImage = contentShape.type === 'image';

                // Match normal-shape behavior: non-interactive when locked and NOT in edit mode
                const groupListening = !(shape.locked && !isEditMode);

                // Optional background hit area (for images) — now respects listening state
                const backgroundHitArea = isMaskedImage ? (
                  <Rect
                    x={-contentShape.width / 2}
                    y={-contentShape.height / 2}
                    width={contentShape.width}
                    height={contentShape.height}
                    fill="rgba(0,0,0,0)"
                    listening={groupListening}
                    perfectDrawEnabled={false}
                    shadowForStrokeEnabled={false}
                    hitStrokeWidth={0}
                  />
                ) : null;

                return (
                  <Group
                    id={shape.id.toString()}
                    key={groupKey}
                    x={outerX}
                    y={outerY}
                    scaleX={outerScaleX}
                    scaleY={outerScaleY}
                    rotation={outerRotation}
                    draggable={selectedTool === 'select' && !shape.locked}
                    listening={groupListening}
                    onMouseDown={(e) => {
                      if (e.evt.button === 1) {
                        e.cancelBubble = true;
                        e.target.stopDrag();
                        return;
                      }
                      handleShapeClick(e, shape.id.toString());
                    }}
                    onTouchStart={(e) => handleShapeClick(e, shape.id.toString())}
                    onDragStart={() => handleDragStartInternal(shape.id.toString())}
                    onDragEnd={(e) =>
                      handleDragEndInternal(shape.id.toString(), {
                        x: e.target.x(),
                        y: e.target.y(),
                      })
                    }
                    onTransformEnd={(e) =>
                      handleTransformEndSnapping(shape.id.toString(), e.target)
                    }
                    dragBoundFunc={createDragBoundFunc(shape.id.toString())}
                  >
                    {/* Full-size transparent hit area (respects groupListening) */}
                    <Rect
                      x={-(contentShape.width * (contentShape.scaleX || 1)) / 2}
                      y={-(contentShape.height * (contentShape.scaleY || 1)) / 2}
                      width={contentShape.width * (contentShape.scaleX || 1)}
                      height={contentShape.height * (contentShape.scaleY || 1)}
                      fill="rgba(0,0,0,0)"
                      listening={groupListening}
                      perfectDrawEnabled={false}
                      shadowForStrokeEnabled={false}
                      hitStrokeWidth={0}
                    />

                    {backgroundHitArea}

                    {/* Clipped content (also mirrors listening) */}
                    <Group
                      listening={groupListening}
                      clipFunc={(ctx) => {
                        const offsetX = (maskShape.x || 0) - contentShape.x;
                        const offsetY = (maskShape.y || 0) - contentShape.y;

                        ctx.beginPath();

                        if (shape.reverseMask) {
                          // outer rect first (for even-odd)
                          ctx.rect(
                            -(contentShape.width * (contentShape.scaleX || 1)) / 2,
                            -(contentShape.height * (contentShape.scaleY || 1)) / 2,
                            contentShape.width * (contentShape.scaleX || 1),
                            contentShape.height * (contentShape.scaleY || 1)
                          );
                        }

                        if (maskShape.type === 'pen' && Array.isArray(maskShape.points)) {
                          const points = maskShape.points;
                          if (points.length >= 2) {
                            ctx.moveTo(points[0] + offsetX, points[1] + offsetY);
                            for (let i = 2; i < points.length; i += 2) {
                              ctx.lineTo(points[i] + offsetX, points[i + 1] + offsetY);
                            }
                            ctx.closePath();
                          }
                        } else if (maskShape.type === 'rect') {
                          ctx.rect(offsetX, offsetY, maskShape.width, maskShape.height);
                        }

                        ctx.clip(shape.reverseMask ? 'evenodd' : 'nonzero');
                      }}
                    >
                      <ImageShape
                        key={contentShape.id}
                        shape={contentShape}
                        x={0}
                        y={0}
                        offsetX={contentShape.width / 2}
                        offsetY={contentShape.height / 2}
                        scaleX={contentShape.scaleX || 1}
                        scaleY={contentShape.scaleY || 1}
                        rotation={contentShape.rotation || 0}
                        width={contentShape.width}
                        height={contentShape.height}
                        opacity={contentShape.opacity ?? 1}
                        onLoad={onImageLoad}
                        logoUrl={logoUrl}
                        squareLogoUrl={squareLogoUrl}
                      />
                    </Group>
                  </Group>
                );
              }



              switch (shape.type) {
                case 'rect': {
                  const strokeStyle = shape.strokeStyle || 'solid';

                  if (strokeStyle === 'wavy' || strokeStyle === 'zigzag') {
                    const width = Math.max(1, shape.width || 1);
                    const height = Math.max(1, shape.height || 1);

                    const fancyPoints = buildFancyRectStrokePoints(
                      width,
                      height,
                      strokeStyle,
                      shape.strokeWaveSize || 8,
                      shape.strokeWaveFrequency || 16
                    );

                    return (
                      <Group {...commonProps}>
                        <Rect
                          width={width}
                          height={height}
                          {...(shape.fillEnabled ? getShapeFillProps(shape) : { fill: 'transparent' })}
                          stroke="transparent"
                          listening={false}
                        />

                        {shape.fillEnabled !== false && (
                          <Rect
                            width={width}
                            height={height}
                            fill="rgba(0,0,0,0.001)"
                            stroke="transparent"
                            listening={!shape.locked}
                          />
                        )}

                        <Line
                          points={fancyPoints}
                          stroke={shape.strokeEnabled !== false ? shape.strokeColor || '#000000' : 'transparent'}
                          strokeWidth={shape.strokeWidth || 2}
                          closed
                          lineCap="round"
                          lineJoin="round"
                          strokeScaleEnabled={false}
                          listening={false}
                        />
                      </Group>
                    );
                  }

                  const isDotted = shape.strokeStyle === 'dotted';

                  return (
                    <Rect
                      {...commonProps}
                      {...shapeSpecificProps}
                      width={shape.width}
                      height={shape.height}
                      lineCap={isDotted ? 'round' : 'square'}
                      lineJoin={isDotted ? 'round' : 'miter'}
                    />
                  );
                }
                case 'roundedRect': {
                  const isDotted = shape.strokeStyle === 'dotted';

                  return (
                    <Rect
                      {...commonProps}
                      {...shapeSpecificProps}
                      width={shape.width}
                      height={shape.height}
                      cornerRadius={shape.cornerRadius || 10}
                      lineCap={isDotted ? 'round' : 'square'}
                      lineJoin={isDotted ? 'round' : 'miter'}
                    />
                  );
                }
                case 'circle': {
                  const isDotted = shape.strokeStyle === 'dotted';

                  return (
                    <Circle
                      {...commonProps}
                      {...shapeSpecificProps}
                      radius={shape.radius}
                      lineCap={isDotted ? 'round' : 'square'}
                      lineJoin={isDotted ? 'round' : 'miter'}
                    />
                  );
                }
                case 'semiCircle': {
                  const isDotted = shape.strokeStyle === 'dotted';

                  return (
                    <Wedge
                      {...commonProps}
                      {...shapeSpecificProps}
                      radius={shape.radius}
                      angle={shape.arcAngle || 180}
                      rotation={shape.rotation || 0}
                      lineCap={isDotted ? 'round' : 'square'}
                      lineJoin={isDotted ? 'round' : 'miter'}
                    />
                  );
                }
                case 'circleSegments':
                  return renderCircleSegmentsShape(shape, commonProps);
                case 'maze':
                  return renderMazeShape(shape, commonProps);
                case 'triangle': {
                  const radius = shape.radius || 1;

                  // Peak position is stored as a percentage from -100 to 100.
                  // -100 aligns with the left base point.
                  // 0 centres the peak.
                  // 100 aligns with the right base point.
                  const peakPositionPercent = Math.max(
                    -100,
                    Math.min(100, Number(shape.trianglePeakOffset) || 0)
                  );

                  const peakOffset =
                    (peakPositionPercent / 100) * radius;

                  return (
                    <Line
                      {...commonProps}
                      {...shapeSpecificProps}
                      points={[
                        peakOffset, -radius,
                        radius, radius,
                        -radius, radius,
                      ]}
                      closed
                      lineJoin="round"
                      lineCap="round"
                    />
                  );
                }
                case 'pentagon':
                  return (
                    <RegularPolygon
                      {...commonProps}
                      {...shapeSpecificProps}
                      sides={5}
                      radius={shape.radius}
                      lineCap={shape.strokeStyle === 'dotted' ? 'round' : 'square'}
                      lineJoin={shape.strokeStyle === 'dotted' ? 'round' : 'miter'}
                    />
                  );
                case 'hexagon':
                  return (
                    <RegularPolygon
                      {...commonProps}
                      {...shapeSpecificProps}
                      sides={6}
                      radius={shape.radius}
                      lineCap={shape.strokeStyle === 'dotted' ? 'round' : 'square'}
                      lineJoin={shape.strokeStyle === 'dotted' ? 'round' : 'miter'}
                    />
                  );
                case 'octagon':
                  return (
                    <RegularPolygon
                      {...commonProps}
                      {...shapeSpecificProps}
                      sides={8}
                      radius={shape.radius}
                      lineCap={shape.strokeStyle === 'dotted' ? 'round' : 'square'}
                      lineJoin={shape.strokeStyle === 'dotted' ? 'round' : 'miter'}
                    />
                  );
                case 'star':
                  return (
                    <Star
                      {...commonProps}
                      {...shapeSpecificProps}
                      numPoints={shape.numPoints || 5}
                      innerRadius={
                        shape.innerRadius > 0
                          ? shape.innerRadius
                          : shape.outerRadius / 2
                      }
                      lineCap={shape.strokeStyle === 'dotted' ? 'round' : 'square'}
                      lineJoin={shape.strokeStyle === 'dotted' ? 'round' : 'miter'}
                      outerRadius={shape.outerRadius}
                    />
                  );
                case 'grid':
                  return renderGridShape(shape, commonProps);
                case 'wordSearch':
                  return renderWordSearchShape(shape, commonProps);  
                case 'line':
                return (
                  <Line
                    {...commonProps}
                    points={shape.points}
                    stroke={shape.strokeColor}
                    strokeWidth={shape.strokeWidth}
                    fill={
                      shape.fillEnabled ? (shape.fillColor || '#ffffff') : undefined
                    }
                    closed={shape.closed || false}
                    lineCap="round"
                    lineJoin="round"
                    dash={shape.dashEnabled ? shape.dash : []}
                    opacity={shape.opacity}
                    hitStrokeWidth={20}
                  />
                );
                case 'pen': {
                  if (shape.bezierEnabled && Array.isArray(shape.bezierPoints)) {
                    const selected = selectedShapeIds.includes(shape.id.toString());

                    const canShowBezierEditHandles =
                      selected &&
                      !isExporting &&
                      (!shape.locked || isEditMode);

                    const drawBezierPath = (ctx, bezierPoints, closed) => {
                      if (!bezierPoints.length) return;

                      ctx.beginPath();
                      ctx.moveTo(bezierPoints[0].x, bezierPoints[0].y);

                      const total = closed ? bezierPoints.length : bezierPoints.length - 1;

                      for (let i = 0; i < total; i++) {
                        const current = bezierPoints[i];
                        const next = bezierPoints[(i + 1) % bezierPoints.length];

                        ctx.bezierCurveTo(
                          current.x + current.out.x,
                          current.y + current.out.y,
                          next.x + next.in.x,
                          next.y + next.in.y,
                          next.x,
                          next.y
                        );
                      }

                      if (closed) ctx.closePath();
                    };

                    const getBezierBounds = () => {
                      const allPoints = [];

                      shape.bezierPoints.forEach((point) => {
                        allPoints.push(
                          { x: point.x, y: point.y },
                          { x: point.x + point.in.x, y: point.y + point.in.y },
                          { x: point.x + point.out.x, y: point.y + point.out.y }
                        );
                      });

                      const minX = Math.min(...allPoints.map((p) => p.x));
                      const minY = Math.min(...allPoints.map((p) => p.y));
                      const maxX = Math.max(...allPoints.map((p) => p.x));
                      const maxY = Math.max(...allPoints.map((p) => p.y));

                      return {
                        x: minX - 20,
                        y: minY - 20,
                        width: maxX - minX + 40,
                        height: maxY - minY + 40,
                      };
                    };

                    const updateBezierPoint = (pointIndex, nextPoint) => {
                      setPages((prevPages) =>
                        prevPages.map((page, pageIndex) => {
                          if (pageIndex !== activePage) return page;

                          return {
                            ...page,
                            shapes: page.shapes.map((s) => {
                              if (s.id.toString() !== shape.id.toString()) return s;

                              const nextBezierPoints = [...s.bezierPoints];

                              nextBezierPoints[pointIndex] = {
                                ...nextBezierPoints[pointIndex],
                                ...nextPoint,
                              };

                              return {
                                ...s,
                                bezierPoints: nextBezierPoints,
                                points: nextBezierPoints.flatMap((point) => [point.x, point.y]),
                                bezierSelectedPointIndex: pointIndex,
                              };
                            }),
                          };
                        })
                      );
                    };

                    const addBezierPointAtPointer = (e) => {
                      if (selectedTool !== 'select') return;
                      if (shape.locked) return;
                      if (!Array.isArray(shape.bezierPoints) || shape.bezierPoints.length < 2) return;

                      e.cancelBubble = true;

                      const stage = stageRef.current;
                      const pointer = stage?.getPointerPosition();
                      const group = e.target.getParent();

                      if (!stage || !pointer || !group) return;

                      const transform = group.getAbsoluteTransform().copy();
                      transform.invert();

                      const localPointer = transform.point(pointer);

                      const bezierPoints = shape.bezierPoints;
                      const total = shape.closed ? bezierPoints.length : bezierPoints.length - 1;

                      let bestSegmentIndex = 0;
                      let bestDistance = Infinity;

                      const cubicPoint = (p0, p1, p2, p3, t) => {
                        const mt = 1 - t;

                        return {
                          x:
                            mt * mt * mt * p0.x +
                            3 * mt * mt * t * p1.x +
                            3 * mt * t * t * p2.x +
                            t * t * t * p3.x,
                          y:
                            mt * mt * mt * p0.y +
                            3 * mt * mt * t * p1.y +
                            3 * mt * t * t * p2.y +
                            t * t * t * p3.y,
                        };
                      };

                      for (let i = 0; i < total; i++) {
                        const current = bezierPoints[i];
                        const next = bezierPoints[(i + 1) % bezierPoints.length];

                        const p0 = { x: current.x, y: current.y };
                        const p1 = {
                          x: current.x + current.out.x,
                          y: current.y + current.out.y,
                        };
                        const p2 = {
                          x: next.x + next.in.x,
                          y: next.y + next.in.y,
                        };
                        const p3 = { x: next.x, y: next.y };

                        for (let step = 0; step <= 30; step++) {
                          const t = step / 30;
                          const sample = cubicPoint(p0, p1, p2, p3, t);
                          const distance = Math.hypot(
                            sample.x - localPointer.x,
                            sample.y - localPointer.y
                          );

                          if (distance < bestDistance) {
                            bestDistance = distance;
                            bestSegmentIndex = i;
                          }
                        }
                      }

                      const current = bezierPoints[bestSegmentIndex];
                      const next = bezierPoints[(bestSegmentIndex + 1) % bezierPoints.length];

                      const dx = next.x - current.x;
                      const dy = next.y - current.y;
                      const handleLength = Math.min(35, Math.max(12, Math.hypot(dx, dy) * 0.18));

                      const newPoint = {
                        x: localPointer.x,
                        y: localPointer.y,
                        in: {
                          x: -dx * 0.18,
                          y: -dy * 0.18,
                        },
                        out: {
                          x: dx * 0.18,
                          y: dy * 0.18,
                        },
                      };

                      if (Math.hypot(newPoint.in.x, newPoint.in.y) > handleLength) {
                        const inLength = Math.hypot(newPoint.in.x, newPoint.in.y);
                        newPoint.in.x = (newPoint.in.x / inLength) * handleLength;
                        newPoint.in.y = (newPoint.in.y / inLength) * handleLength;
                      }

                      if (Math.hypot(newPoint.out.x, newPoint.out.y) > handleLength) {
                        const outLength = Math.hypot(newPoint.out.x, newPoint.out.y);
                        newPoint.out.x = (newPoint.out.x / outLength) * handleLength;
                        newPoint.out.y = (newPoint.out.y / outLength) * handleLength;
                      }

                      pushToUndoStack();

                      setPages((prevPages) =>
                        prevPages.map((page, pageIndex) => {
                          if (pageIndex !== activePage) return page;

                          return {
                            ...page,
                            shapes: page.shapes.map((s) => {
                              if (s.id.toString() !== shape.id.toString()) return s;

                              const nextBezierPoints = [...s.bezierPoints];
                              nextBezierPoints.splice(bestSegmentIndex + 1, 0, newPoint);

                              return {
                                ...s,
                                bezierPoints: nextBezierPoints,
                                points: nextBezierPoints.flatMap((point) => [point.x, point.y]),
                              };
                            }),
                          };
                        })
                      );

                      setSelectedBezierPoint({
                        shapeId: shape.id.toString(),
                        pointIndex: bestSegmentIndex + 1,
                      });
                    };

                    const updateHandleFromPointer = (e, pointIndex, handleType) => {
                      const stage = stageRef.current;
                      if (!stage) return;

                      const pointer = stage.getPointerPosition();
                      if (!pointer) return;

                      const group = e.target.getParent()?.getParent();
                      if (!group) return;

                      const transform = group.getAbsoluteTransform().copy();
                      transform.invert();

                      const localPointer = transform.point(pointer);
                      const point = shape.bezierPoints[pointIndex];

                      const getShiftConstrainedHandle = () => {
                        const dx = localPointer.x - point.x;
                        const dy = localPointer.y - point.y;
                        const distance = Math.hypot(dx, dy);

                        if (!isShiftPressed || distance === 0) {
                          return { x: dx, y: dy };
                        }

                        const snapStep = Math.PI / 8;
                        const angle = Math.atan2(dy, dx);
                        const snappedAngle = Math.round(angle / snapStep) * snapStep;

                        return {
                          x: Math.cos(snappedAngle) * distance,
                          y: Math.sin(snappedAngle) * distance,
                        };
                      };

                      if (handleType === 'anchor') {
                        const shiftIsDown = isShiftPressed || e?.evt?.shiftKey;

                        if (!shiftIsDown) {
                          updateBezierPoint(pointIndex, {
                            x: localPointer.x,
                            y: localPointer.y,
                          });
                          return;
                        }

                        const dragStart = bezierAnchorDragStartRef.current;

                        const startPoint =
                          dragStart &&
                          dragStart.shapeId === shape.id.toString() &&
                          dragStart.pointIndex === pointIndex
                            ? dragStart
                            : point;

                        const dx = localPointer.x - startPoint.x;
                        const dy = localPointer.y - startPoint.y;

                        const constrainedPoint =
                          Math.abs(dx) >= Math.abs(dy)
                            ? {
                                x: localPointer.x,
                                y: startPoint.y,
                              }
                            : {
                                x: startPoint.x,
                                y: localPointer.y,
                              };

                        updateBezierPoint(pointIndex, constrainedPoint);
                        return;
                      }

                      if (handleType === 'in') {
                        const constrainedHandle = getShiftConstrainedHandle();

                        updateBezierPoint(pointIndex, {
                          in: constrainedHandle,
                          ...(shape.bezierSymmetricalHandles && {
                            out: {
                              x: -constrainedHandle.x,
                              y: -constrainedHandle.y,
                            },
                          }),
                        });
                        return;
                      }

                      if (handleType === 'out') {
                        const constrainedHandle = getShiftConstrainedHandle();

                        updateBezierPoint(pointIndex, {
                          out: constrainedHandle,
                          ...(shape.bezierSymmetricalHandles && {
                            in: {
                              x: -constrainedHandle.x,
                              y: -constrainedHandle.y,
                            },
                          }),
                        });
                      }
                    };

                    const bounds = getBezierBounds();

                    const bezierShapeScaleX = Math.abs(shape.scaleX || 1);
                    const bezierShapeScaleY = Math.abs(shape.scaleY || 1);
                    const bezierAverageShapeScale =
                      (bezierShapeScaleX + bezierShapeScaleY) / 2 || 1;

                    // Keeps Bezier edit UI usable when the canvas zooms
                    // AND when the Bezier shape itself is scaled smaller.
                    const bezierUiScale =
                      1 / Math.max((scale || 1) * bezierAverageShapeScale, 0.01);

                    const bezierAnchorRadius = 7 * bezierUiScale;
                    const bezierHandleRadius = 6 * bezierUiScale;
                    const bezierHandleStrokeWidth = Math.max(0.75 * bezierUiScale, 0.75);
                    const bezierGuideStrokeWidth = Math.max(0.75 * bezierUiScale, 0.75);
                    const bezierGuideDash = [4 * bezierUiScale, 4 * bezierUiScale];
                    return (
                      <Group
                        {...commonProps}
                        draggable={!shape.locked && selectedTool === 'select'}
                      >
                        {/* No invisible bounds rect here.
                          Selection and dragging should come from the real Bezier path,
                          not from a hidden rectangular hit box. */}

                        {/* Invisible Bezier selection helper.
    This gives the curve a generous path-shaped hit area without using a rectangle. */}
    <Shape
      strokeScaleEnabled={false}
      perfectDrawEnabled={false}
      sceneFunc={(ctx, konvaShape) => {
        drawBezierPath(ctx, shape.bezierPoints, shape.closed);
        ctx.fillStrokeShape(konvaShape);
      }}
      stroke="rgba(0,0,0,0.001)"
      strokeWidth={Math.max(34, (shape.strokeWidth || 2) + 24)}
      fill={
        shape.closed && shape.fillEnabled
          ? 'rgba(0,0,0,0.001)'
          : 'transparent'
      }
      lineCap="round"
      lineJoin="round"
      listening={!shape.locked}
      onDblClick={addBezierPointAtPointer}
      onMouseDown={(e) => {
        if (selectedTool !== 'select') return;

        e.cancelBubble = true;

        handleShapeClick(e, shape.id.toString());

        const stage = stageRef.current;
        const pointer = stage?.getPointerPosition();
        const group = e.target.getParent();

        if (group && pointer && !shape.locked) {
          bezierPendingDragRef.current = {
            group,
            shapeId: shape.id.toString(),
            startX: pointer.x,
            startY: pointer.y,
          };
        }
      }}
    />

    {/* Invisible anchor point selection helpers.
        These make clicking near individual Bezier points select the shape. */}
    {shape.bezierPoints.map((point, index) => (
      <Circle
        key={`${shape.id}-bezier-anchor-hit-${index}`}
        x={point.x}
        y={point.y}
        radius={14 * bezierUiScale}
        fill="rgba(0,0,0,0.001)"
        stroke="transparent"
        listening={!shape.locked}
        onMouseDown={(e) => {
          if (selectedTool !== 'select') return;

          e.cancelBubble = true;

          handleShapeClick(e, shape.id.toString());

          setSelectedBezierPoint({
            shapeId: shape.id.toString(),
            pointIndex: index,
          });

          setPages((prevPages) =>
            prevPages.map((page, pageIndex) => {
              if (pageIndex !== activePage) return page;

              return {
                ...page,
                shapes: page.shapes.map((s) => {
                  if (s.id.toString() !== shape.id.toString()) return s;

                  return {
                    ...s,
                    bezierSelectedPointIndex: index,
                  };
                }),
              };
            })
          );

          const stage = stageRef.current;
          const pointer = stage?.getPointerPosition();
          const group = e.target.getParent();

          if (group && pointer && !shape.locked) {
            bezierPendingDragRef.current = {
              group,
              shapeId: shape.id.toString(),
              startX: pointer.x,
              startY: pointer.y,
            };
          }
        }}
      />
    ))}

    {/* Visible Bezier path. This is now drawing only; selection is handled above. */}
    <Shape
      strokeScaleEnabled={false}
      listening={false}
      sceneFunc={(ctx, konvaShape) => {
        drawBezierPath(ctx, shape.bezierPoints, shape.closed);
        ctx.fillStrokeShape(konvaShape);
      }}
      stroke={shape.strokeEnabled !== false ? shape.strokeColor : 'transparent'}
      strokeWidth={shape.strokeWidth}
      fill={
        shape.fillEnabled && shape.closed
          ? shape.fillColor || '#ffffff'
          : 'transparent'
      }
      {...(shape.fillEnabled && shape.closed ? getShapeFillProps(shape) : {})}
      dash={
        shape.dashEnabled
          ? (shape.dash?.length ? shape.dash : [10, 5])
          : []
      }
      opacity={shape.opacity}
    />

                        

                        {canShowBezierEditHandles && shape.bezierPoints.map((point, index) => {
                          const isSelectedBezierPoint =
                            selectedBezierPoint?.shapeId === shape.id.toString() &&
                            selectedBezierPoint?.pointIndex === index;

                          return (
                          <Group key={`${shape.id}-bezier-${index}`}>
                            {isSelectedBezierPoint && (() => {
                              const collapsedOffset = 16 * bezierUiScale;
                              const collapseDistance = 8 * bezierUiScale;

                              const inActualX = point.x + point.in.x;
                              const inActualY = point.y + point.in.y;
                              const outActualX = point.x + point.out.x;
                              const outActualY = point.y + point.out.y;

                              const inIsCollapsed =
                                Math.hypot(inActualX - point.x, inActualY - point.y) <= collapseDistance;

                              const outIsCollapsed =
                                Math.hypot(outActualX - point.x, outActualY - point.y) <= collapseDistance;

                              const inDisplayX = inIsCollapsed ? point.x - collapsedOffset : inActualX;
                              const inDisplayY = inIsCollapsed ? point.y : inActualY;
                              const outDisplayX = outIsCollapsed ? point.x + collapsedOffset : outActualX;
                              const outDisplayY = outIsCollapsed ? point.y : outActualY;

                              return (
                                <Line
                                  points={[
                                    inDisplayX,
                                    inDisplayY,
                                    point.x,
                                    point.y,
                                    outDisplayX,
                                    outDisplayY,
                                  ]}
                                  stroke="#888"
                                  strokeWidth={bezierGuideStrokeWidth}
                                  dash={bezierGuideDash}
                                  listening={false}
                                />
                              );
                            })()}

                            {(() => {
                              const collapsedOffset = 16 * bezierUiScale;
                              const collapseDistance = 8 * bezierUiScale;

                              const inActualX = point.x + point.in.x;
                              const inActualY = point.y + point.in.y;
                              const outActualX = point.x + point.out.x;
                              const outActualY = point.y + point.out.y;

                              const inIsCollapsed =
                                Math.hypot(inActualX - point.x, inActualY - point.y) <= collapseDistance;

                              const outIsCollapsed =
                                Math.hypot(outActualX - point.x, outActualY - point.y) <= collapseDistance;

                              const handles = [
                                {
                                  type: 'in',
                                  x: inIsCollapsed ? point.x - collapsedOffset : inActualX,
                                  y: inIsCollapsed ? point.y : inActualY,
                                  actualX: inActualX,
                                  actualY: inActualY,
                                  fill: '#ffffff',
                                },
                                {
                                  type: 'out',
                                  x: outIsCollapsed ? point.x + collapsedOffset : outActualX,
                                  y: outIsCollapsed ? point.y : outActualY,
                                  actualX: outActualX,
                                  actualY: outActualY,
                                  fill: '#ffffff',
                                },
                                {
                                  type: 'anchor',
                                  x: point.x,
                                  y: point.y,
                                  actualX: point.x,
                                  actualY: point.y,
                                  fill:
                                    selectedBezierPoint?.shapeId === shape.id.toString() &&
                                    selectedBezierPoint?.pointIndex === index
                                      ? '#ff6b6b'
                                      : '#ffcc00',
                                },
                              ];

                            return handles
                              .filter((handle) => handle.type === 'anchor' || isSelectedBezierPoint)
                              .map((handle) => (
                              <Circle
                                key={`${shape.id}-${index}-${handle.type}`}
                                x={handle.x}
                                y={handle.y}
                                radius={handle.type === 'anchor' ? bezierAnchorRadius : bezierHandleRadius}
                                fill={handle.fill}
                                stroke="#000000"
                                strokeWidth={bezierHandleStrokeWidth}
                                draggable={selectedTool === 'select' && (!shape.locked || isEditMode)}
                                dragDistance={0}
                                dragBoundFunc={() => ({
                                  x: handle.x,
                                  y: handle.y,
                                })}
                                onMouseDown={(e) => {
                                  e.cancelBubble = true;

                                  bezierPendingDragRef.current = null;
                                  clickThroughDragRef.current = null;

                                  handleShapeClick(e, shape.id.toString());

                                  if (handle.type === 'anchor') {
                                    setSelectedBezierPoint({
                                      shapeId: shape.id.toString(),
                                      pointIndex: index,
                                    });

                                    setPages((prevPages) =>
                                      prevPages.map((page, pageIndex) => {
                                        if (pageIndex !== activePage) return page;

                                        return {
                                          ...page,
                                          shapes: page.shapes.map((s) => {
                                            if (s.id.toString() !== shape.id.toString()) return s;

                                            return {
                                              ...s,
                                              bezierSelectedPointIndex: index,
                                            };
                                          }),
                                        };
                                      })
                                    );
                                  }
                                }}
                                onDragStart={(e) => {
                                  e.cancelBubble = true;

                                  bezierPendingDragRef.current = null;
                                  clickThroughDragRef.current = null;

                                  if (handle.type === 'anchor') {
                                    bezierAnchorDragStartRef.current = {
                                      shapeId: shape.id.toString(),
                                      pointIndex: index,
                                      x: point.x,
                                      y: point.y,
                                    };
                                  } else {
                                    bezierAnchorDragStartRef.current = null;
                                  }

                                  pushToUndoStack();
                                }}
                                onDragMove={(e) => {
                                  e.cancelBubble = true;

                                  updateHandleFromPointer(e, index, handle.type);

                                  e.target.position({
                                    x: handle.x,
                                    y: handle.y,
                                  });
                                }}
                                onDragEnd={(e) => {
                                  e.cancelBubble = true;

                                  updateHandleFromPointer(e, index, handle.type);

                                  bezierAnchorDragStartRef.current = null;

                                  e.target.position({
                                    x: handle.x,
                                    y: handle.y,
                                  });
                                }}
                              />
                              ));
                            })()}
                          </Group>
                          );
                        })}
                      </Group>
                    );
                  }

                  return (
                    <Line
                      {...commonProps}
                      points={shape.points}
                      stroke={shape.strokeEnabled !== false ? shape.strokeColor : 'transparent'}
                      strokeWidth={shape.strokeWidth}
                      fill={
                        shape.fillEnabled && shape.closed
                          ? shape.fillColor || '#ffffff'
                          : 'transparent'
                      }
                      closed={shape.closed || false}
                      lineCap="round"
                      lineJoin="round"
                      dash={
                        shape.dashEnabled
                          ? (shape.dash?.length ? shape.dash : [10, 5])
                          : []
                      }
                      opacity={shape.opacity}
                      hitStrokeWidth={20}
                    />
                  );
                }

                          

                  case 'pencil':
                    const { stroke, ...commonWithoutStroke } = commonProps;
                    return (
                      <Line
                        {...commonWithoutStroke}
                        points={shape.points}
                        stroke={shape.strokeColor}
                        strokeWidth={shape.strokeWidth}
                        lineCap={shape.lineCap}
                        lineJoin={shape.lineJoin}
                        dash={shape.dashEnabled ? shape.dash : []}
                        opacity={shape.opacity}
                        hitStrokeWidth={20}
                      />
                    );          
                case 'image':
                  return (
                    <ImageShape
                      {...commonProps}
                      shape={shape}
                      onLoad={onImageLoad}
                      logoUrl={logoUrl}
                      squareLogoUrl={squareLogoUrl}
                    />
                  );
                case 'text':
                  return (
                    <Text
                      {...commonProps}
                      text={shape.text}
                      fontSize={shape.fontSize}
                      fontFamily={shape.fontFamily}
                      fill={shape.fill}
                      fontStyle={`${shape.bold ? 'bold' : ''} ${shape.italic ? 'italic' : ''}`}
                      decoration={shape.underline ? 'underline' : ''}
                      opacity={shape.opacity}
                      align={shape.align || 'left'}
                      wrap="none"
                      lineHeight={1}
                    />
                  );
                default:
                  return null;
              }
            })}

            {/* 🖊️ Show live Pen Tool line while drawing */}
            {selectedTool === 'pen' && penPoints.length > 0 && (
              <Line
                points={penPoints.flat()}
                stroke="#000000"
                strokeWidth={2}
                lineJoin="round"
                dash={[10, 4]}
                closed={false}
              />
            )}

            {/* Rubber-band preview line */}
            {selectedTool === 'pen' && penPoints.length > 0 && penMousePos && (
              <Line
                points={[
                  ...penPoints[penPoints.length - 1],
                  penMousePos.x,
                  penMousePos.y,
                ]}
                stroke="#888888"
                strokeWidth={2}
                dash={[4, 4]}
                lineCap="round"
                lineJoin="round"
              />
            )}


            {selectionRect && !isExporting && (
              <Rect
                x={Math.min(
                  selectionRect.x,
                  selectionRect.x + selectionRect.width
                )}
                y={Math.min(
                  selectionRect.y,
                  selectionRect.y + selectionRect.height
                )}
                width={Math.abs(selectionRect.width)}
                height={Math.abs(selectionRect.height)}
                fill="rgba(0, 161, 255, 0.3)"
                stroke="#00A1FF"
                dash={[4, 4]}
              />
            )}

            {!isExporting && selectedShapeIds.length > 0 && (
              <Transformer
                ref={transformerRef}
                {...transformerProps}
                rotateAnchorCursor={`url(${cursorRotate}) 15 15, grab`}
                rotateAnchorOffset={dynamicRotateAnchorOffset}
                anchorSize={dynamicAnchorSize}
                padding={dynamicTransformerPadding}
                enabledAnchors={dynamicEnabledAnchors}
                anchorStyleFunc={(anchor) => {
                  const anchorKey = getAnchorKey(anchor);
                  if (!anchorKey || anchorKey === 'rotater') return;

                  anchor.width(dynamicAnchorSize);
                  anchor.height(dynamicAnchorSize);
                  anchor.offsetX(dynamicAnchorSize / 2);
                  anchor.offsetY(dynamicAnchorSize / 2);
                  anchor.fill('#ffffff');
                  anchor.stroke('#000000');
                  anchor.strokeWidth(1);
                  anchor.cornerRadius(2);

                  anchor.off('mouseenter mouseleave');
                  anchor.on('mouseenter', () => {
                    setStageCursor(getTransformCursor(anchorKey));
                  });
                  anchor.on('mouseleave', () => {
                    setStageCursor(getBaseCursorStyle());
                  });
                }}
                onTransformEnd={() => {
                  transformStartedRef.current = false;
                  setStageCursor(getBaseCursorStyle());
                }}
              />
            )}
                    </Layer>

          {isMeasurementRulerVisible && !isExporting && (
            <Layer>
              {renderMeasurementRuler()}

              {isMeasurementRulerSelected && (
                <Transformer
                  ref={measurementRulerTransformerRef}
                  rotateEnabled={true}
                  resizeEnabled={false}
                  enabledAnchors={[]}
                  borderEnabled={true}
                  borderStroke="#7c5a22"
                  borderStrokeWidth={1}
                  borderDash={[5, 4]}
                  rotateAnchorOffset={40}
                  anchorSize={14}
                  anchorStroke="#7c5a22"
                  anchorFill="#ffffff"
                  anchorCornerRadius={20}
                  flipEnabled={false}
                  shouldOverdrawWholeArea={false}
                  onTransform={() => {
                    const node = measurementRulerRef.current;

                    if (!node) return;

                    node.scaleX(1);
                    node.scaleY(1);

                    const rawRotation = node.rotation();

                    const nextRotation = isShiftPressed
                      ? Math.round(rawRotation / 22.5) * 22.5
                      : rawRotation;

                    node.rotation(nextRotation);

                    setMeasurementRulerSettings((prev) => ({
                      ...prev,
                      x: node.x(),
                      y: node.y(),
                      rotation: nextRotation,
                    }));
                  }}
                  onTransformEnd={() => {
                    const node = measurementRulerRef.current;

                    if (!node) return;

                    node.scaleX(1);
                    node.scaleY(1);

                    const rawRotation = node.rotation();

                    const finalRotation = isShiftPressed
                      ? Math.round(rawRotation / 22.5) * 22.5
                      : rawRotation;

                    node.rotation(finalRotation);

                    setMeasurementRulerSettings((prev) => ({
                      ...prev,
                      x: node.x(),
                      y: node.y(),
                      rotation: finalRotation,
                    }));
                  }}
                />
              )}
            </Layer>
          )}

          {!isExporting && canvasBursts?.length > 0 && (
            <Layer listening={false}>
              {canvasBursts.map((burst) => (
                <CanvasBurst
                  key={burst.id}
                  burst={burst}
                  stageRef={stageRef}
                  setCanvasBursts={setCanvasBursts}
                />
              ))}
            </Layer>
          )}

        </Stage>        
      </div>
    </div>
  );
};

export default CanvasArea;
